/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import './CourseDetails.css';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';
import SyllabusAnalysis from './SyllabusAnalysis';
import { isReadOnly } from '../config/academicYear';

const CourseDetails = () => {
  const { programId, courseCode: rawCourseCode } = useParams();
  const courseCode = encodeURIComponent(rawCourseCode);
  const navigate = useNavigate();
  const [courseDetails, setCourseDetails] = useState(null);
  const [courseId, setCourseId] = useState(null);
  const [learningOutcomes, setLearningOutcomes] = useState([]);
  const [weeklyTopics, setWeeklyTopics] = useState([]);
  const [aktsActivities, setAktsActivities] = useState([]);
  const [editAkts, setEditAkts] = useState(false);
  const [aktsDraft, setAktsDraft] = useState([]);
  const [evaluationSystem, setEvaluationSystem] = useState([]);
  const [editEval, setEditEval] = useState(false);
  const [evalDraft, setEvalDraft] = useState([]);
  const [teachingStaff, setTeachingStaff] = useState([]);
  const [editStaff, setEditStaff] = useState(false);
  const [staffDraft, setStaffDraft] = useState([]);
  const [programLearningOutcomes, setProgramLearningOutcomes] = useState([]);
  const [sustainableDevelopmentGoals, setSustainableDevelopmentGoals] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [courseResources, setCourseResources] = useState(null);
  const [otherInfo, setOtherInfo] = useState(null);
  const [editOther, setEditOther] = useState(false);
  const [otherDraft, setOtherDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectionWarning, setSectionWarning] = useState('');
  const [includeWeeklyNotesForAnalysis, setIncludeWeeklyNotesForAnalysis] = useState(false);
  
  // Haftalık notlar modal state'leri
  const [isWeekModalOpen, setIsWeekModalOpen] = useState(false);
  const [selectedWeekNumber, setSelectedWeekNumber] = useState(null);
  const [weekNotes, setWeekNotes] = useState({});
  const [weekVideos, setWeekVideos] = useState({});
  const [userType, setUserType] = useState(sessionStorage.getItem('userRole') || '');
  // Ders izlencesi analizi gömülü; sadece analizi tetikliyoruz
  const [isSyllabusAccordionOpen] = useState(true);
  // Tüm izlence tablolarını bir tuşla aç/kapat
  const [showSyllabusTables, setShowSyllabusTables] = useState(false);
  const syllabusAnalysisRef = useRef(null);

  // Word upload state
  const [wordUploadLoading, setWordUploadLoading] = useState(false);
  const [wordUploadMessage, setWordUploadMessage] = useState(null);
  const wordFileInputRef = useRef(null);

  // İzlence yükleme yetkisi
  const [syllabusUploadEnabled, setSyllabusUploadEnabled] = useState(true);
  // Ders bilgisi alanlarının dolu olması tek başına izlencenin başarıyla
  // işlendiği anlamına gelmez. Yükleme akışının zorunlu iki temel bölümü
  // okunabiliyorsa kullanıcıya "kayıtlı izlence" durumu gösterilir.
  const hasStoredSyllabus = weeklyTopics.length > 0 && learningOutcomes.length > 0;

  useEffect(() => {
    const fetchCourseDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        setSectionWarning('');

        // Dersin kendisi bulunmuyorsa alt bölümleri çağırıp aynı 404'ü
        // kullanıcıya defalarca göstermeyelim.
        const detailsResponse = await fetch(
          `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/course-info`
        );
        if (!detailsResponse.ok) {
          setCourseDetails(null);
          setError(detailsResponse.status === 404
            ? 'Bu ders seçili akademik yılda bu programda bulunamadı. Ders listesinden yeniden seçim yapın.'
            : 'Ders bilgileri şu anda yüklenemiyor. Sayfayı yenileyip tekrar deneyin.');
          return;
        }
        
        // Ders mevcutsa kalan bölümleri paralel olarak çek. İzlencenin
        // herhangi bir alt bölümü eksikse veya tek bir istek geçici olarak
        // başarısız olursa ders sayfasının tamamını hata durumuna sokma.
        const fetchOptionalSection = async (url) => {
          try {
            return await fetch(url);
          } catch (requestError) {
            console.warn('Ders alt bölümü yüklenemedi:', url, requestError);
            return null;
          }
        };

        const [
          learningOutcomesResponse,
          weeklyTopicsResponse,
          aktsActivitiesResponse,
          evaluationSystemResponse,
          teachingStaffResponse,
          programLearningOutcomesResponse,
          sustainableDevelopmentGoalsResponse,
          courseResourcesResponse,
          otherInfoResponse
        ] = await Promise.all([
          fetchOptionalSection(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/learning-outcomes`),
          fetchOptionalSection(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-topics`),
          fetchOptionalSection(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/akts-activities`),
          fetchOptionalSection(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/evaluation-system`),
          fetchOptionalSection(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/teaching-staff`),
          fetchOptionalSection(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/program-learning-outcomes`),
          fetchOptionalSection(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/sustainable-development-goals`),
          fetchOptionalSection(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/course-resources`),
          fetchOptionalSection(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/other-info`)
        ]);

        const sectionResponses = [learningOutcomesResponse, weeklyTopicsResponse,
          aktsActivitiesResponse, evaluationSystemResponse, teachingStaffResponse,
          programLearningOutcomesResponse, sustainableDevelopmentGoalsResponse,
          courseResourcesResponse, otherInfoResponse];
        if (sectionResponses.some(response => !response?.ok)) {
          setSectionWarning('Dersin bazı bölümleri şu anda görüntülenemiyor. Eksik bilgileri daha sonra tekrar kontrol edin.');
        }

        // Güvenli JSON parse fonksiyonu
        const safeJsonParse = async (response, defaultValue = null) => {
          try {
            if (!response?.ok) {
              console.warn(`API response not ok: ${response.status}`);
              return defaultValue;
            }
            const text = await response.text();
            if (!text.trim()) {
              return defaultValue;
            }
            return JSON.parse(text);
          } catch (error) {
            console.warn('JSON parse error:', error);
            return defaultValue;
          }
        };

        // Her response'u güvenli şekilde parse et
        const details = await safeJsonParse(detailsResponse, {});
        const learningOutcomesData = await safeJsonParse(learningOutcomesResponse, []);
        const weeklyTopicsData = await safeJsonParse(weeklyTopicsResponse, []);
        const aktsActivitiesData = await safeJsonParse(aktsActivitiesResponse, []);
        const evaluationSystemData = await safeJsonParse(evaluationSystemResponse, []);
        const teachingStaffData = await safeJsonParse(teachingStaffResponse, []);
        const programLearningOutcomesData = await safeJsonParse(programLearningOutcomesResponse, []);
        console.log('Program Learning Outcomes Data:', programLearningOutcomesData);
        console.log('Program Learning Outcomes count:', programLearningOutcomesData?.length);
        if (programLearningOutcomesData && programLearningOutcomesData.length > 0) {
          console.log('First outcome contribution_levels:', programLearningOutcomesData[0]?.contribution_levels);
        }
        const sustainableDevelopmentGoalsData = await safeJsonParse(sustainableDevelopmentGoalsResponse, []);
        const courseResourcesData = await safeJsonParse(courseResourcesResponse, null);
        const otherInfoData = await safeJsonParse(otherInfoResponse, null);

        setCourseDetails(details);
        // Try to infer courseId from details; if not present, resolve via courses list
        let resolvedCourseId = details?.id || details?.course_id || null;
        if (!resolvedCourseId) {
          try {
            const coursesRes = await fetch(`${API_BASE_URL}/api/courses?programId=${programId}`);
            const courses = await safeJsonParse(coursesRes, []);
            const found = (courses || []).find(c => (c.code || c.course_code) === rawCourseCode);
            if (found && found.id) {
              resolvedCourseId = found.id;
            }
          } catch (e) {
            // ignore
          }
        }
        setCourseId(resolvedCourseId);
        
        // Learning outcomes'u düzgün parse et ve duplicate'leri filtrele
        if (learningOutcomesData && learningOutcomesData.learning) {
          const seenLearningOutcomeKeys = new Set();
          const dedupedLearningOutcomes = [];

          learningOutcomesData.learning.forEach(lo => {
            const sanitizedIds = normalizeProgramOutcomeIds(lo.program_outcome_ids);

            const normalizedLo = {
              ...lo,
              program_outcome_ids: sanitizedIds
            };

            const keyParts = [
              normalizedLo.id ? `id:${normalizedLo.id}` : null,
              normalizedLo.number ? `num:${normalizedLo.number}` : null,
              normalizedLo.code ? `code:${normalizedLo.code}` : null,
              (normalizedLo.description || normalizedLo.name || '').trim().toLowerCase()
                ? `desc:${(normalizedLo.description || normalizedLo.name || '').trim().toLowerCase()}`
                : null
            ].filter(Boolean);
            const key = keyParts.join('|') || JSON.stringify(normalizedLo);
            if (!seenLearningOutcomeKeys.has(key)) {
              seenLearningOutcomeKeys.add(key);
              dedupedLearningOutcomes.push(normalizedLo);
            }
          });

          setLearningOutcomes(dedupedLearningOutcomes);
        } else {
          setLearningOutcomes([]);
        }
        
        // Weekly topics'i düzgün parse et
        if (weeklyTopicsData && typeof weeklyTopicsData === 'object') {
          const topicsArray = Object.keys(weeklyTopicsData)
            .filter(key => !isNaN(key)) // Sadece sayısal key'leri al
            .map(key => ({
              number: parseInt(key),
              topic: weeklyTopicsData[key].topic,
              on_hazirlik: weeklyTopicsData[key].on_hazirlik,
              metodoloji: weeklyTopicsData[key].metodoloji,
              ders_notlari: weeklyTopicsData[key].ders_notlari || weeklyTopicsData[key].course_notes
            }))
            .sort((a, b) => a.number - b.number); // Sırala
          setWeeklyTopics(topicsArray);
        } else {
          setWeeklyTopics([]);
        }
        
        setAktsActivities(aktsActivitiesData || []);
        setEvaluationSystem(evaluationSystemData || []);
        setTeachingStaff(teachingStaffData || []);
        // Duplicate'leri filtrele (outcome_number veya number'a göre)
        const uniqueOutcomes = [];
        const seenNumbers = new Set();
        (programLearningOutcomesData || []).forEach(outcome => {
          const outcomeNum = outcome.outcome_number || outcome.number;
          if (outcomeNum && !seenNumbers.has(outcomeNum)) {
            seenNumbers.add(outcomeNum);
            uniqueOutcomes.push(outcome);
          } else if (!outcomeNum) {
            // Number yoksa direkt ekle (id'ye göre kontrol et)
            const outcomeId = outcome.id;
            if (!uniqueOutcomes.find(o => o.id === outcomeId)) {
              uniqueOutcomes.push(outcome);
            }
          }
        });
        setProgramLearningOutcomes(uniqueOutcomes);
        setSustainableDevelopmentGoals(sustainableDevelopmentGoalsData || []);
        
        setCourseResources(courseResourcesData);
        setOtherInfo(otherInfoData);
        setAktsDraft((aktsActivitiesData || []).map(a => ({...a})));
        setEvalDraft((evaluationSystemData || []).map(a => ({...a})));
        setStaffDraft((teachingStaffData || []).map(a => ({...a})));
        setOtherDraft(otherInfoData || {});
        setCourseInfoDraft(details || {});
        setProgramLevelsDraft((programLearningOutcomesData || []).map(o => [...(o.contribution_levels || [])]));
        setSdgLevelsDraft((sustainableDevelopmentGoalsData || []).map(g => [...(g.contribution_levels || [])]));
        setSdgDraft((sustainableDevelopmentGoalsData || []).map(g => g.goal_description || ''));
        
      } catch (error) {
        console.error('Ders detayları yüklenirken hata:', error);
        setError('Sayfa yüklenirken beklenmeyen bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseDetails();

    // İzlence yükleme yetkisini kontrol et
    fetch(API_ENDPOINTS.SYLLABUS_UPLOAD_PERMISSION)
      .then(r => r.json())
      .then(data => { if (data?.success) setSyllabusUploadEnabled(data.enabled !== false); })
      .catch(() => {});
  }, [programId, courseCode]);

  // Haftalık notları yükle
  useEffect(() => {
    if (programId && courseCode) {
      fetchWeeklyNotes();
    }
  }, [programId, courseCode]);

  const normalizeProgramOutcomeIds = (ids) => {
    return Array.from(
      new Set(
        (ids || [])
          .map(id => parseInt(id))
          .filter(id => !isNaN(id))
      )
    );
  };

  const getUniqueProgramOutcomeIds = (outcome) => normalizeProgramOutcomeIds(outcome?.program_outcome_ids);

  const getProgramOutcomeOptionById = (id) => {
    const match = programOutcomes.find(po => parseInt(po.id) === parseInt(id));
    if (!match) {
      return { value: String(id), label: `PÇ${id}` };
    }
    const code = match.code || `PÇ${match.number}` || `PÇ${match.id}`;
    const description = match.description || match.name || '';
    const label = description ? `${code} - ${description}` : code;
    return { value: String(match.id), label };
  };

  const getSelectedProgramOutcomeOptions = (outcome) => {
    if (!programOutcomes || programOutcomes.length === 0) {
      return [];
    }
    const selectedIds = getUniqueProgramOutcomeIds(outcome);
    if (selectedIds.length === 0) {
      return [];
    }
    
    // Önce options array'ini oluştur (Select component'inde kullanılan formatla AYNI)
    const options = programOutcomes.map(po => {
      const code = po.code || `PÇ${po.number}` || `PÇ${po.id}`;
      const description = po.description || po.name || '';
      const label = description ? `${code} - ${description}` : code;
      return { value: String(po.id), label };
    });
    
    // Seçili ID'ler için options array'inden objeleri al (TAM EŞLEŞME İÇİN)
    const selectedOptions = selectedIds
      .map(id => {
        const idStr = String(id);
        // Options array'inde tam eşleşme ara
        const found = options.find(opt => opt.value === idStr);
        if (found) {
          return found;
        }
        // Integer eşleşmesi için de dene
        const idInt = parseInt(id);
        if (!isNaN(idInt)) {
          const foundInt = options.find(opt => parseInt(opt.value) === idInt);
          if (foundInt) {
            return foundInt;
          }
        }
        return null;
      })
      .filter(opt => opt !== null);
    
    // Duplicate'leri kaldır
    const seenValues = new Set();
    return selectedOptions.filter(opt => {
      if (seenValues.has(opt.value)) return false;
      seenValues.add(opt.value);
      return true;
    });
  };

  // Program çıktılarını yükle
  useEffect(() => {
    if (programId) {
      fetch(`${API_BASE_URL}/api/program-outcomes/${programId}`)
        .then(res => res.json())
        .then(data => {
          if (data.outcomes) {
            const sorted = data.outcomes.sort((a, b) => {
              const aNum = parseInt(a.code?.replace('PÇ', '') || a.number || '0');
              const bNum = parseInt(b.code?.replace('PÇ', '') || b.number || '0');
              return aNum - bNum;
            });
            setProgramOutcomes(sorted);
          }
        })
        .catch(err => console.error('Program çıktıları yüklenirken hata:', err));
    }
  }, [programId]);

  const parseWeekValue = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const m = value.match(/(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    }
    if (!Number.isNaN(Number(value))) {
      return Number(value);
    }
    return 0;
  };

  // Haftalık notları çek
  const fetchWeeklyNotes = async () => {
    if (!programId || !courseCode) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-notes?t=${Date.now()}`,
        {
          cache: 'no-store',
          headers: {
            'User-Type': userType || 'guest',
          },
        }
      );

      if (!res.ok) {
        return;
      }

      const list = await res.json();
      const groupedNotes = {};

      (list || []).forEach((item) => {
        const rawWeek = item.week_number || item.week || item.weekName || '';
        const weekNum = parseWeekValue(rawWeek);
        if (!groupedNotes[weekNum]) groupedNotes[weekNum] = [];
        groupedNotes[weekNum].push({
          ...item,
          name: item.name || item.file_name || 'Dosya',
        });
      });

      setWeekNotes(groupedNotes);
      // Video API'si henüz hazır olmadığı için videoları boş bırakıyoruz
      setWeekVideos({});
    } catch (err) {
      console.error('Haftalık notlar yüklenirken hata:', err);
    }
  };

  // Modal aç/kapat
  const openWeekModal = (weekNumber) => {
    setSelectedWeekNumber(weekNumber);
    setIsWeekModalOpen(true);
    fetchWeeklyNotes();
  };

  const closeWeekModal = () => {
    setIsWeekModalOpen(false);
    setSelectedWeekNumber(null);
  };

  // Video linki sil
  const handleDeleteVideoLink = async (weekNumber, videoId) => {
    if (isReadOnly()) { alert('Arşiv akademik yılında dosya veya video bağlantısı silinemez.'); return; }
    if (!window.confirm('Video linkini silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-notes/videos/${videoId}`,
        {
          method: 'DELETE',
          headers: {
            'User-Type': userType || 'admin'
          }
        }
      );

      if (response.ok) {
        await fetchWeeklyNotes();
        alert('Video linki başarıyla silindi');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Silme başarısız' }));
        alert(errorData.error || 'Silme başarısız');
      }
    } catch (err) {
      console.error('Video linki silme hatası:', err);
      alert('Video linki silinirken bir hata oluştu');
    }
  };

  // Program çıktısı eşlemesi kaydetme
  const handleMappingChange = async (learningOutcomeId, selectedProgramOutcomeIds) => {
    try {
      setLoadingMapping(true);
      console.log('[DEBUG] Eşleme kaydediliyor:', { learningOutcomeId, selectedProgramOutcomeIds, programId, courseId });
      
      // learningOutcomeId'nin integer olduğundan emin ol
      const loId = parseInt(learningOutcomeId);
      if (isNaN(loId)) {
        console.error('[DEBUG] Geçersiz learning outcome ID:', learningOutcomeId);
        alert('Geçersiz Dersin Öğrenme Çıktısı ID\'si');
        return;
      }
      
      if (!programId || !courseId) {
        alert('Program ID veya Course ID bulunamadı');
        return;
      }
      const normalizedSelectedIds = normalizeProgramOutcomeIds(selectedProgramOutcomeIds);
      
      const response = await fetch(`${API_BASE_URL}/api/learning-outcomes/${loId}/program-outcomes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          program_id: programId,
          course_id: courseId,
          program_outcome_ids: normalizedSelectedIds 
        })
      });
      
      const responseData = await response.json();
      console.log('[DEBUG] Backend yanıtı:', response.status, responseData);
      
      if (response.ok) {
        // Dersin öğrenme çıktılarını güncelle
        setLearningOutcomes(prev => {
          const updated = prev.map(lo => 
            lo.id === loId 
              ? { ...lo, program_outcome_ids: normalizedSelectedIds }
              : lo
          );
          console.log('[DEBUG] State güncellendi:', { loId, normalizedSelectedIds, updatedOutcome: updated.find(lo => lo.id === loId) });
          return updated;
        });
        
        // Program Öğrenme Çıktılarını (katkı düzeylerini) yeniden yükle
        try {
          const programLearningOutcomesResponse = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/program-learning-outcomes`);
          if (programLearningOutcomesResponse.ok) {
            const text = await programLearningOutcomesResponse.text();
            if (text.trim()) {
              const programLearningOutcomesData = JSON.parse(text);
              // Duplicate'leri filtrele
              const uniqueOutcomes = [];
              const seenNumbers = new Set();
              (programLearningOutcomesData || []).forEach(outcome => {
                const outcomeNum = outcome.outcome_number || outcome.number;
                if (outcomeNum && !seenNumbers.has(outcomeNum)) {
                  seenNumbers.add(outcomeNum);
                  uniqueOutcomes.push(outcome);
                } else if (!outcomeNum) {
                  const outcomeId = outcome.id;
                  if (!uniqueOutcomes.find(o => o.id === outcomeId)) {
                    uniqueOutcomes.push(outcome);
                  }
                }
              });
              setProgramLearningOutcomes(uniqueOutcomes);
              setProgramLevelsDraft((programLearningOutcomesData || []).map(o => [...(o.contribution_levels || [])]));
              console.log('[DEBUG] Program öğrenme çıktıları güncellendi (katkı düzeyleri)');
            }
          }
        } catch (err) {
          console.error('[DEBUG] Program öğrenme çıktıları güncellenemedi:', err);
        }
      } else {
        const errorMsg = responseData.error || 'Eşleme kaydedilemedi';
        console.error('[DEBUG] Backend hatası:', errorMsg);
        alert(errorMsg);
      }
    } catch (err) {
      console.error('[DEBUG] Eşleme kaydedilirken hata:', err);
      alert('Eşleme kaydedilirken bir hata oluştu: ' + err.message);
    } finally {
      setLoadingMapping(false);
    }
  };

  // Yapay zeka önerisi iste - ai_suggestions.py endpoint'ini kullan
  const requestAiSuggestions = async (learningOutcome) => {
    if (!programId || !learningOutcome || !programOutcomes.length) return;
    
    setAiSuggestions(prev => ({
      ...prev,
      [learningOutcome.id]: { loading: true, error: '', suggestions: [] }
    }));

    try {
      // Dersin Öğrenme Çıktısını formatla
      const learningOutcomeFormatted = {
        id: String(learningOutcome.id),
        code: learningOutcome.code || learningOutcome.number || '',
        description: learningOutcome.description || learningOutcome.name || ''
      };

      // Program çıktılarını formatla
      const programOutcomesFormatted = programOutcomes.map(po => ({
        id: String(po.id),
        code: po.code || `PÇ${po.number}` || '',
        description: po.description || po.name || ''
      }));

      const response = await fetch(`${API_BASE_URL}/api/ai/suggest-program-outcomes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          learning_outcome: learningOutcomeFormatted,
          program_outcomes: programOutcomesFormatted
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Uyum oranına göre sırala (yüksekten düşüğe)
        const sortedSuggestions = (data.suggestions || []).sort((a, b) => 
          (b.confidence || 0) - (a.confidence || 0)
        );

        setAiSuggestions(prev => ({
          ...prev,
          [learningOutcome.id]: {
            loading: false,
            suggestions: sortedSuggestions,
            overallReasoning: data.overall_reasoning || ''
          }
        }));
        
        // Öneriler geldiğinde otomatik aç
        if (sortedSuggestions.length > 0) {
          setExpandedAiSuggestions(prev => ({
            ...prev,
            [learningOutcome.id]: true
          }));
        }
      } else {
        setAiSuggestions(prev => ({
          ...prev,
          [learningOutcome.id]: {
            loading: false,
            error: data.error || 'Yapay zeka önerisi alınamadı',
            suggestions: []
          }
        }));
      }
    } catch (err) {
      console.error('[DEBUG] Yapay zeka önerisi hatası:', err);
      setAiSuggestions(prev => ({
        ...prev,
        [learningOutcome.id]: {
          loading: false,
          error: 'Yapay zeka önerisi alınırken bir hata oluştu: ' + err.message,
          suggestions: []
        }
      }));
    }
  };

  // Dersin Öğrenme Çıktısı uyum analizi (amaç/içerik ile)
  const requestLoAlignment = async (outcome) => {
    if (!courseDetails || !outcome) return;

    setAiLoAssessments(prev => ({
      ...prev,
      [outcome.id]: { loading: true, error: '', items: [] }
    }));

    try {
      const payload = {
        course: {
          course_name: courseDetails.course_name,
          program_name: courseDetails.program_name || courseDetails.cd_program,
          aim: courseDetails.aim || otherInfo?.aim || '',
          content: courseDetails.content || otherInfo?.content || ''
        },
        learning_outcome: {
          id: outcome.id,
          code: outcome.code || outcome.number || '',
          description: outcome.description || outcome.name || ''
        },
        program_outcomes: (programOutcomes || []).map(po => ({
          code: po.code || po.number,
          number: po.number,
          description: po.description || po.name || ''
        })),
        evaluation: (evaluationSystem || []).map(ev => ({
          activity_name: ev.activity_name || ev.name || '',
          count: ev.count || '',
          contribution_share: ev.contribution_share || ''
        })),
        weekly_topics: (weeklyTopics || []).map(w => ({
          week: w.number || w.week,
          topic: w.topic || ''
        }))
      };

      const res = await fetch(API_ENDPOINTS.AI_ASSESS_LO_ALIGNMENT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        setAiLoAssessments(prev => ({
          ...prev,
          [outcome.id]: {
            loading: false,
            error: '',
            items: data.items || [],
            overallReasoning: data.overall_reasoning || ''
          }
        }));
      } else {
        setAiLoAssessments(prev => ({
          ...prev,
          [outcome.id]: {
            loading: false,
            error: data.error || 'Yapay Zeka uyum analizi alınamadı.',
            items: []
          }
        }));
      }
    } catch (err) {
      setAiLoAssessments(prev => ({
        ...prev,
        [outcome.id]: {
          loading: false,
          error: err?.message || 'Yapay Zeka uyum analizi alınamadı.',
          items: []
        }
      }));
    }
  };

  const applyLoTextSuggestion = (outcomeId, text) => {
    if (!text) return;
    const idx = (learningOutcomes || []).findIndex(o => o.id === outcomeId);
    if (idx === -1) return;

    // İlk cümleyi veya 240 karaktere kadar olan kısmı uygula
    const sentenceEnd = text.indexOf('.') !== -1 ? text.indexOf('.') + 1 : text.length;
    const firstChunk = text.slice(0, sentenceEnd).trim();
    const trimmed = (firstChunk && firstChunk.length <= 240)
      ? firstChunk
      : text.slice(0, 240).trim();

    const draft = [...learningDraft];
    draft[idx] = trimmed;
    setLearningDraft(draft);
    setEditLearning(true);
  };

  // Yapay zeka önerisini uygula
  const applyAISuggestion = async (learningOutcomeId, suggestion) => {
    try {
      console.log('[DEBUG] Yapay zeka önerisi uygulanıyor:', { learningOutcomeId, suggestion });
      
      const currentOutcome = learningOutcomes.find(lo => lo.id === learningOutcomeId);
      const currentIds = (currentOutcome?.program_outcome_ids || []).map(id => parseInt(id));
      const newId = parseInt(suggestion.program_outcome_id);
      
      console.log('[DEBUG] Mevcut ID\'ler:', currentIds, 'Yeni ID:', newId);
      
      if (isNaN(newId)) {
        console.error('[DEBUG] Geçersiz program_outcome_id:', suggestion.program_outcome_id);
        alert('Geçersiz program çıktısı ID\'si');
        return;
      }
      
      if (!currentIds.includes(newId)) {
        const updatedIds = [...currentIds, newId];
        console.log('[DEBUG] Güncellenmiş ID\'ler:', updatedIds);
        await handleMappingChange(learningOutcomeId, updatedIds);
      } else {
        console.log('[DEBUG] Program çıktısı zaten seçili');
      }
    } catch (err) {
      console.error('[DEBUG] Yapay zeka önerisi uygulanırken hata:', err);
      alert('Yapay zeka önerisi uygulanırken bir hata oluştu: ' + err.message);
    }
  };

  // Dersin amacı/içeriği için AI payload oluştur
  const buildCourseAiPayload = () => {
    const courseInfoForAi = {
      name: courseDetails?.course_name || '',
      code: courseDetails?.course_code || rawCourseCode || '',
      program_name: courseDetails?.program_name || courseDetails?.cd_program || '',
      faculty_name: courseDetails?.enstitu_fakulte || '',
    };

    const programOutcomesForAi = (programOutcomes || []).map((po, idx) => ({
      code: po.code || `PÇ${po.number || idx + 1}`,
      description: po.description || po.name || '',
    }));

    const evaluationForAi = (evaluationSystem || []).map((ev) => ({
      activity_name: ev.activity_name || ev.name || '',
      count: ev.count || '',
      contribution_share: ev.contribution_share || '',
    }));

    const weeklyTopicsForAi = (weeklyTopics || []).map((topic) => ({
      week: topic.number || topic.week || '',
      topic: topic.topic || '',
      preparation: topic.on_hazirlik || '',
      methodology: topic.metodoloji || '',
    }));

    return {
      course: courseInfoForAi,
      program_outcomes: programOutcomesForAi,
      evaluation: evaluationForAi,
      weekly_topics: weeklyTopicsForAi,
    };
  };

  const handleWordUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setWordUploadMessage({ type: 'error', text: 'Sadece .docx dosyaları yüklenebilir.' });
      return;
    }
    setWordUploadLoading(true);
    setWordUploadMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/upload-word`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setWordUploadMessage({ type: 'success', text: 'İzlence başarıyla yüklendi ve bilgiler dolduruldu. Sayfa yenileniyor...' });
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setWordUploadMessage({ type: 'error', text: data.error || 'Yükleme sırasında hata oluştu.' });
      }
    } catch (err) {
      setWordUploadMessage({ type: 'error', text: 'Sunucuya bağlanılamadı.' });
    } finally {
      setWordUploadLoading(false);
    }
  };

  // Dersin amacı/içeriği için Yapay Zeka önerisi iste
  const requestCourseTextSuggestion = async (sectionKey) => {
    if (!courseDetails) {
      setAiTextErrors((prev) => ({ ...prev, [sectionKey]: 'Ders verisi yüklenmeden Yapay Zeka önerisi alınamaz.' }));
      return;
    }

    setAiTextErrors((prev) => ({ ...prev, [sectionKey]: '' }));
    setAiTextLoading((prev) => ({ ...prev, [sectionKey]: true }));

    try {
      const payload = {
        section: sectionKey,
        ...buildCourseAiPayload(),
      };

      const res = await fetch(API_ENDPOINTS.AI_SUGGEST_COURSE_TEXT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        setAiTextResults((prev) => ({
          ...prev,
          [sectionKey]: {
            suggestion: data.suggestion,
            summary: data.summary,
            freshness: typeof data.freshness === 'number' ? data.freshness : undefined,
          },
        }));
        setAiPanelsOpen((prev) => ({ ...prev, [sectionKey]: true }));
      } else {
        setAiTextErrors((prev) => ({ ...prev, [sectionKey]: data.error || 'Yapay Zeka önerisi alınamadı.' }));
      }
    } catch (err) {
      setAiTextErrors((prev) => ({ ...prev, [sectionKey]: err?.message || 'Yapay Zeka önerisi alınamadı.' }));
    } finally {
      setAiTextLoading((prev) => ({ ...prev, [sectionKey]: false }));
    }
  };

  const toggleAiPanel = (sectionKey) => {
    setAiPanelsOpen((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const applyAiText = (sectionKey) => {
    const aiText = aiTextResults?.[sectionKey]?.suggestion;
    if (!aiText) return;

    if (sectionKey === 'aim') {
      setAimDraft(aiText);
      setEditAim(true);
    } else if (sectionKey === 'content') {
      setContentDraft(aiText);
      setEditContent(true);
    }
  };

  // Permission check for syllabus editing
  const [canEditSyllabus, setCanEditSyllabus] = useState(false);
  const [learningOutcomesEditEnabled, setLearningOutcomesEditEnabled] = useState(true);
  
  useEffect(() => {
    const akademisyenId = sessionStorage.getItem('akademisyen_id');
    if (!akademisyenId) return;
    fetch(`${API_BASE_URL}/api/admin/academic-syllabus-permission/${akademisyenId}`)
      .then(r => r.json())
      .then(data => setCanEditSyllabus(Boolean(data?.success && data?.has_permission)))
      .catch(() => setCanEditSyllabus(false));
  }, []);
  
  // Global öğrenme çıktıları düzenleme yetkisi kontrolü
  useEffect(() => {
    const fetchLearningOutcomesEditFlag = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/learning-outcomes-edit-permission`);
        const data = await res.json();
        if (data?.success) setLearningOutcomesEditEnabled(data.enabled !== false);
      } catch (e) {
        console.error('Öğrenme çıktıları düzenleme yetkisi flag alınamadı:', e);
      }
    };
    fetchLearningOutcomesEditFlag();
  }, []);
  
  // Her ikisi de gerekli: hem akademisyen izni hem de global ayar açık olmalı
  const canEditLearningOutcomes = canEditSyllabus && learningOutcomesEditEnabled && !isReadOnly();

  // Edit states and save handlers
  const [editLearning, setEditLearning] = useState(false);
  const [learningDraft, setLearningDraft] = useState([]);
  const [editWeekly, setEditWeekly] = useState(false);
  const [weeklyDraft, setWeeklyDraft] = useState([]);
  const [editProgram, setEditProgram] = useState(false);
  const [programDraft, setProgramDraft] = useState([]);
  const [programLevelsDraft, setProgramLevelsDraft] = useState([]);
  const [editCourseInfo, setEditCourseInfo] = useState(false);
  const [courseInfoDraft, setCourseInfoDraft] = useState({});
  const [editSDG, setEditSDG] = useState(false);
  const [sdgLevelsDraft, setSdgLevelsDraft] = useState([]);
  const [sdgDraft, setSdgDraft] = useState([]);
  const [editAim, setEditAim] = useState(false);
  const [aimDraft, setAimDraft] = useState('');
  const [editContent, setEditContent] = useState(false);
  const [contentDraft, setContentDraft] = useState('');
  const [editResources, setEditResources] = useState(false);
  const [resourcesDraft, setResourcesDraft] = useState({ course_resources: '', recommended_resources: '' });
  const [editWorkload, setEditWorkload] = useState(false);
  const [workloadDraft, setWorkloadDraft] = useState({ total_workload: '', total_workload_per_30: '', akts_credit: '' });
  
  // Program çıktıları ve eşleme için state'ler
  const [programOutcomes, setProgramOutcomes] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState({});
const [aiLoAssessments, setAiLoAssessments] = useState({});
  const [loadingMapping, setLoadingMapping] = useState(false);
  const [expandedAiSuggestions, setExpandedAiSuggestions] = useState({});
  const [aiTextLoading, setAiTextLoading] = useState({ aim: false, content: false });
  const [aiTextErrors, setAiTextErrors] = useState({ aim: '', content: '' });
  const [aiTextResults, setAiTextResults] = useState({ aim: null, content: null });
  const [aiPanelsOpen, setAiPanelsOpen] = useState({ aim: false, content: false });

  useEffect(() => {
    setLearningDraft((learningOutcomes || []).map(o => o.name || o.description || ''));
    setWeeklyDraft((weeklyTopics || []).map(t => ({
      topic: t.topic || '',
      on_hazirlik: t.on_hazirlik || '',
      metodoloji: t.metodoloji || ''
    })));
    setProgramDraft((programLearningOutcomes || []).map(o => o.outcome_description || o.description || ''));
    setAimDraft(courseDetails?.aim || otherInfo?.aim || '');
    setContentDraft(courseDetails?.content || otherInfo?.content || '');
    setResourcesDraft({
      course_resources: courseDetails?.course_resources || otherInfo?.course_resources || '',
      recommended_resources: courseDetails?.recommended_resources || otherInfo?.recommended_resources || ''
    });
    setWorkloadDraft({
      total_workload: courseDetails?.total_workload || '',
      total_workload_per_30: courseDetails?.total_workload_per_30 || '',
      akts_credit: courseDetails?.akts_credit || ''
    });
  }, [learningOutcomes, weeklyTopics, programLearningOutcomes, courseDetails, otherInfo]);

  const saveLearning = async () => {
    if (isReadOnly()) { alert('Arşiv akademik yılında öğrenme çıktıları düzenlenemez.'); return; }
    if (!courseId) { alert('Ders kimliği bulunamadı.'); return; }
    try {
      const payload = {
        learning_outcomes: (learningOutcomes || []).map((o, i) => ({
          id: o.id,
          description: learningDraft[i]
        }))
      };
      const res = await fetch(`${API_BASE_URL}/api/course-outcomes/${courseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setEditLearning(false);
        setLearningOutcomes((learningOutcomes || []).map((o, i) => ({ ...o, name: undefined, description: learningDraft[i] })));
      } else {
        alert('Dersin öğrenme çıktıları kaydedilemedi');
      }
    } catch {
      alert('Dersin öğrenme çıktıları kaydedilemedi');
    }
  };

  const saveWeekly = async () => {
    if (!courseId) { alert('Ders kimliği bulunamadı.'); return; }
    try {
      const weekly_topics = (weeklyTopics || []).map((t, i) => ({
        week: t.number || t.week,
        topic: weeklyDraft[i]?.topic || '',
        on_hazirlik: weeklyDraft[i]?.on_hazirlik || '',
        metodoloji: weeklyDraft[i]?.metodoloji || ''
      }));
      const res = await fetch(`${API_BASE_URL}/api/weekly-topics/${courseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weekly_topics })
      });
      if (res.ok) {
        setEditWeekly(false);
        setWeeklyTopics((weeklyTopics || []).map((t, i) => ({
          ...t,
          topic: weeklyDraft[i]?.topic || '',
          on_hazirlik: weeklyDraft[i]?.on_hazirlik || '',
          metodoloji: weeklyDraft[i]?.metodoloji || ''
        })));
      } else {
        alert('Haftalık konular kaydedilemedi');
      }
    } catch {
      alert('Haftalık konular kaydedilemedi');
    }
  };

  const saveProgram = async () => {
    try {
      // Sadece id'si olan outcome'ları gönder
      const outcomes = (programLearningOutcomes || [])
        .map((o, i) => ({ id: o.id, description: programDraft[i] }))
        .filter(o => o.id); // id yoksa filtrele
      
      if (outcomes.length > 0) {
        const res = await fetch(`${API_BASE_URL}/api/program-outcomes/${programId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ outcomes })
        });
        if (!res.ok) {
          alert('Program çıktıları kaydedilemedi');
          return;
        }
      }
      
      // Save contribution levels ve outcome descriptions
      const levelsRes = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/program-learning-outcomes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outcomes: (programLearningOutcomes || []).map((o, i) => ({
            serial_number: o.outcome_number,
            outcome_description: programDraft[i] || '',
            contribution_levels: programLevelsDraft[i] || []
          }))
        })
      });
      if (levelsRes.ok) {
        setEditProgram(false);
        setProgramLearningOutcomes((programLearningOutcomes || []).map((o, i) => ({ 
          ...o, 
          outcome_description: programDraft[i] || '',
          contribution_levels: programLevelsDraft[i] || [] 
        })));
      } else {
        alert('Katkı düzeyleri kaydedilemedi');
      }
    } catch (error) {
      console.error('Program çıktıları kaydedilirken hata:', error);
      alert('Program çıktıları kaydedilemedi');
    }
  };

  const saveCourseInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/course-info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(courseInfoDraft)
      });
      if (res.ok) {
        setEditCourseInfo(false);
        setCourseDetails({...courseInfoDraft});
      } else {
        alert('Ders bilgileri kaydedilemedi');
      }
    } catch {
      alert('Ders bilgileri kaydedilemedi');
    }
  };

  const saveSDG = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/sustainable-development-goals`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          goals: (sustainableDevelopmentGoals || []).map((g, i) => {
            const origIndex = sustainableDevelopmentGoals.indexOf(g);
            return {
              goal_number: g.goal_number,
              goal_description: sdgDraft[origIndex] || '',
              contribution_levels: sdgLevelsDraft[origIndex] || []
            };
          })
        })
      });
      if (res.ok) {
        setEditSDG(false);
        // origIndex'e göre güncelle
        setSustainableDevelopmentGoals((sustainableDevelopmentGoals || []).map((g) => {
          const origIndex = sustainableDevelopmentGoals.indexOf(g);
          return { 
            ...g, 
            goal_description: sdgDraft[origIndex] !== undefined && sdgDraft[origIndex] !== null ? sdgDraft[origIndex] : g.goal_description,
            contribution_levels: sdgLevelsDraft[origIndex] || [] 
          };
        }));
      } else {
        alert('Sürdürülebilir kalkınma amaçları kaydedilemedi');
      }
    } catch (error) {
      console.error('SDG kaydedilirken hata:', error);
      alert('Sürdürülebilir kalkınma amaçları kaydedilemedi');
    }
  };

  const toggleProgramLevel = (outcomeIndex, level) => {
    const newDraft = [...programLevelsDraft];
    if (!newDraft[outcomeIndex]) newDraft[outcomeIndex] = [];
    // Eğer seçili seviye zaten seçiliyse, onu kaldır (hiçbir şey seçili olmayacak)
    // Eğer seçili değilse, sadece o seviyeyi seç (diğerlerini kaldır)
    if (newDraft[outcomeIndex].includes(level)) {
      newDraft[outcomeIndex] = [];
    } else {
      newDraft[outcomeIndex] = [level];
    }
    setProgramLevelsDraft(newDraft);
  };

  const toggleSDGLevel = (goalIndex, level) => {
    const newDraft = [...sdgLevelsDraft];
    if (!newDraft[goalIndex]) newDraft[goalIndex] = [];
    // Eğer seçili seviye zaten seçiliyse, onu kaldır (hiçbir şey seçili olmayacak)
    // Eğer seçili değilse, sadece o seviyeyi seç (diğerlerini kaldır)
    if (newDraft[goalIndex].includes(level)) {
      newDraft[goalIndex] = [];
    } else {
      newDraft[goalIndex] = [level];
    }
    setSdgLevelsDraft(newDraft);
  };

  // Save handlers for AKTS, Evaluation, Staff, Other
  const saveAkts = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/akts-activities`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(aktsDraft)
      });
      if (res.ok) {
        setEditAkts(false);
        setAktsActivities(aktsDraft.map(a => ({...a})));
      } else { alert('AKTS güncellenemedi'); }
    } catch { alert('AKTS güncellenemedi'); }
  };

  const saveEval = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/evaluation-system`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(evalDraft)
      });
      if (res.ok) {
        setEditEval(false);
        setEvaluationSystem(evalDraft.map(a => ({...a})));
      } else { alert('Değerlendirme sistemi güncellenemedi'); }
    } catch { alert('Değerlendirme sistemi güncellenemedi'); }
  };

  const saveStaff = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/teaching-staff`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(staffDraft)
      });
      if (res.ok) {
        setEditStaff(false);
        setTeachingStaff(staffDraft.map(a => ({...a})));
      } else { alert('Öğretim elemanları güncellenemedi'); }
    } catch { alert('Öğretim elemanları güncellenemedi'); }
  };

  const saveOther = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/other-info`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(otherDraft)
      });
      if (res.ok) {
        setEditOther(false);
        setOtherInfo({...otherDraft});
      } else { alert('Diğer bilgiler güncellenemedi'); }
    } catch { alert('Diğer bilgiler güncellenemedi'); }
  };

  const saveAim = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/course-info`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ aim: aimDraft })
      });
      if (res.ok) {
        setEditAim(false);
        setCourseDetails({...courseDetails, aim: aimDraft});
      } else { alert('Dersin Amacı güncellenemedi'); }
    } catch { alert('Dersin Amacı güncellenemedi'); }
  };

  const saveContent = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/course-info`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ content: contentDraft })
      });
      if (res.ok) {
        setEditContent(false);
        setCourseDetails({...courseDetails, content: contentDraft});
      } else { alert('Dersin İçeriği güncellenemedi'); }
    } catch { alert('Dersin İçeriği güncellenemedi'); }
  };

  const saveResources = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/course-info`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify(resourcesDraft)
      });
      if (res.ok) {
        setEditResources(false);
        setCourseDetails({...courseDetails, ...resourcesDraft});
      } else { alert('Ders Kaynakları güncellenemedi'); }
    } catch { alert('Ders Kaynakları güncellenemedi'); }
  };

  const saveWorkload = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/course-info`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify(workloadDraft)
      });
      if (res.ok) {
        setEditWorkload(false);
        setCourseDetails({...courseDetails, ...workloadDraft});
      } else { alert('İş Yükü Bilgileri güncellenemedi'); }
    } catch { alert('İş Yükü Bilgileri güncellenemedi'); }
  };

  const renderAiHelper = (sectionKey) => {
    const labels = { aim: 'Dersin Amacı', content: 'Dersin İçeriği' };
    const descriptions = {
      aim: 'Ders amacını Yapay Zeka ile ölçer, bağlamı analiz eder.',
      content: 'Ders içeriğini Yapay Zeka ile günceller, haftalık akışla eşleştirir.'
    };
    const isOpen = aiPanelsOpen[sectionKey];
    const result = aiTextResults[sectionKey];
    const loadingState = aiTextLoading[sectionKey];
    const errorText = aiTextErrors[sectionKey];
    const freshness = typeof result?.freshness === 'number' ? Math.max(0, Math.min(result.freshness, 100)) : null;

    return (
      <div className="ai-helper">
        <div className="ai-helper-bar">
          <div className="ai-helper-copy">
                <strong>Yapay Zeka Önerisi</strong>
                <span>
                  {descriptions[sectionKey] || 'Ders verilerini analiz ederek öneri üretir.'}
                </span>
                {freshness !== null && (
                  <div className="ai-freshness">
                    <div className="ai-freshness-label">Güncellik: %{freshness}</div>
                    <div className="ai-freshness-bar">
                      <div className="ai-freshness-bar-fill" style={{ width: `${freshness}%` }} />
                    </div>
                  </div>
                )}
          </div>
          <div className="ai-helper-actions">
            <button className="ai-secondary-btn icon-only" onClick={() => toggleAiPanel(sectionKey)} aria-label="AI panelini aç/kapat">
              <span className={`ai-toggle-arrow ${isOpen ? 'open' : ''}`} />
            </button>
            <button
              className="ai-primary-btn"
              disabled={loadingState}
              onClick={() => requestCourseTextSuggestion(sectionKey)}
            >
              {loadingState ? 'Yapay Zeka çalışıyor…' : `${labels[sectionKey]} için Yapay Zeka önerisi`}
            </button>
          </div>
        </div>
        {isOpen && (
          <div className="ai-helper-panel">
            <p className="ai-helper-description">
              {labels[sectionKey]} için kısa açıklama: Yapay Zeka, ders adı ve bölüm bilgisiyle birlikte
              program hedefleri, ölçme yöntemleri ve haftalık konuları tarayarak güncel küresel yaklaşımlara göre metin üretir.
            </p>
            {errorText && <div className="ai-helper-error">{errorText}</div>}
            {result?.summary && (
              <div className="ai-helper-summary">
                {result.summary}
              </div>
            )}
            {result?.suggestion && (
              <div className="ai-helper-suggestion">
                <div className="ai-helper-suggestion-text">
                  {result.suggestion}
                </div>
                {canEditSyllabus && (
                  <button className="ai-primary-btn" onClick={() => applyAiText(sectionKey)}>
                    Metni alana uygula
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="course-details-container">
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="course-details-container">
        <button onClick={() => navigate(-1)} className="back-button">← Geri Dön</button>
        <div className="error" role="alert">{error}</div>
      </div>
    );
  }

  if (!courseDetails) {
    return (
      <div className="course-details-container">
        <div className="error">Ders bulunamadı</div>
      </div>
    );
  }

  return (
    <div className="course-details-container">
      {sectionWarning && <div className="api-error-banner" role="status">{sectionWarning}</div>}
      <div className="course-details-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Geri Dön
        </button>
        <h1>Ders Detayları</h1>
      </div>

      <div className="course-details-content">
        {/* Ders İzlencesi Analizi - Akordeon */}
        <div className="course-details-section syllabus-accordion">
          <div className="syllabus-accordion-header">
            <div className="syllabus-accordion-title" style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h2 style={{ margin: 0 }}>Ders İzlencesi Analizi</h2>
                  </div>
                  <p style={{ margin: '4px 0 0' }}>Ders izlencesini haftalık notlar dahil isteğe bağlı analiz et.</p>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 10px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '12px',
                  background: '#f1f5f9',
                  boxShadow: '0 2px 6px rgba(15,23,42,0.08)',
                  fontSize: '13px',
                  color: '#0f172a',
                  cursor: 'pointer'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={includeWeeklyNotesForAnalysis}
                  onChange={(e) => setIncludeWeeklyNotesForAnalysis(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ whiteSpace: 'nowrap' }}>Haftalık ders notlarını dahil et</span>
              </label>
              <button
                type="button"
                className="syllabus-accordion-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!courseId) {
                    alert('Ders bulunamadı. Lütfen sayfayı yenileyin.');
                    return;
                  }
                  // Ref üzerinden direkt analizi tetikle
                  syllabusAnalysisRef.current?.startAnalysis?.();
                }}
              >
                Analiz Et
              </button>
            </div>
          </div>
          <div className="syllabus-accordion-body">
            <SyllabusAnalysis
              apiUrl={API_BASE_URL}
              preselectedCourseId={courseId}
              isEmbedded
              includeWeeklyNotesProp={includeWeeklyNotesForAnalysis}
              onIncludeWeeklyNotesChange={setIncludeWeeklyNotesForAnalysis}
              ref={syllabusAnalysisRef}
            />
          </div>
        </div>

        {/* İzlence şablonu, yükleme ve görüntüleme */}
        <div className="course-details-section">
          <div className="syllabus-upload-card">
            <div>
              <h2 style={{ marginBottom: 4 }}>Ders İzlencesi</h2>
              <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
                Şablonu doldurup Word (.docx) dosyasını yükleyin veya mevcut izlenceyi görüntüleyin.
              </p>
              {hasStoredSyllabus && (
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#166534', fontWeight: 600 }}>
                  ✓ Bu ders için seçili akademik yılda kayıtlı izlence var. Yeniden yükleme yalnızca bilgileri güncellemek için gereklidir.
                </p>
              )}
            </div>
            <a
              className="syllabus-template-download"
              href={`${process.env.PUBLIC_URL || ''}/izlence-sablonu.docx`}
              download="İzlence Şablonu.docx"
            >
              İzlence Şablonunu İndir
            </a>
            <div className="syllabus-upload-actions">
              <input
                ref={wordFileInputRef}
                type="file"
                accept=".docx"
                style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files[0]) handleWordUpload(e.target.files[0]); e.target.value = ''; }}
              />
              <button
                type="button"
                className="edit-btn word-upload-btn"
                disabled={wordUploadLoading || !syllabusUploadEnabled || isReadOnly()
                  || !['admin', 'akademisyen'].includes(userType)}
                title={!syllabusUploadEnabled ? 'İzlence yükleme yetkisi admin tarafından kapatılmıştır' : undefined}
                onClick={() => wordFileInputRef.current?.click()}
              >
                {wordUploadLoading ? 'Yükleniyor...' : 'İzlence Yükle'}
              </button>
              <button
                type="button"
                className="edit-btn view-syllabus-btn"
                onClick={() => setShowSyllabusTables(prev => !prev)}
              >
                {showSyllabusTables ? 'İzlenceyi Gizle' : 'İzlenceyi Göster'}
              </button>
            </div>
          </div>
          {wordUploadMessage && (
            <div style={{
              marginTop: 10,
              padding: '8px 14px',
              borderRadius: 6,
              fontSize: 14,
              background: wordUploadMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
              color: wordUploadMessage.type === 'success' ? '#166534' : '#991b1b',
            }}>
              {wordUploadMessage.text}
            </div>
          )}
        </div>

        {showSyllabusTables && (
        <>
        {/* 1. Ders Bilgileri */}
        <div className="course-details-section">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <h2>Ders Bilgileri</h2>
            {canEditSyllabus && (
              editCourseInfo ? (
                <div className="button-group">
                  <button className="cancel-btn" onClick={() => { setEditCourseInfo(false); setCourseInfoDraft(courseDetails || {}); }}>İptal</button>
                  <button className="save-btn" onClick={saveCourseInfo}>Kaydet</button>
                </div>
              ) : (
                <button className="edit-btn" onClick={() => setEditCourseInfo(true)}>Düzenle</button>
              )
            )}
          </div>
          <table className="course-details-table">
            <tbody>
              <tr>
                <td className="table-label">Ders Kodu:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.course_code || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, course_code: e.target.value})} readOnly />
                ) : (courseDetails.course_code || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Ders Adı:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.course_name || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, course_name: e.target.value})} />
                ) : (courseDetails.course_name || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Enstitü/Fakülte/MYO:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.enstitu_fakulte || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, enstitu_fakulte: e.target.value})} />
                ) : (courseDetails.enstitu_fakulte || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Program:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.cd_program || courseInfoDraft.program_name || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, cd_program: e.target.value, program_name: e.target.value})} readOnly />
                ) : (courseDetails.cd_program || courseDetails.program_name || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Öğretim Düzeyi:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.ogretim_duzeyi || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, ogretim_duzeyi: e.target.value})} />
                ) : (courseDetails.ogretim_duzeyi || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Öğretim Türü:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.ogretim_turu || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, ogretim_turu: e.target.value})} />
                ) : (courseDetails.ogretim_turu || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Dönem:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input type="number" style={{width:'100%'}} value={courseInfoDraft.donem || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, donem: e.target.value})} />
                ) : (courseDetails.donem || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Teorik:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input type="number" style={{width:'100%'}} value={courseInfoDraft.teorik !== null && courseInfoDraft.teorik !== undefined && courseInfoDraft.teorik !== '' ? courseInfoDraft.teorik : ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, teorik: e.target.value})} />
                ) : (courseDetails.teorik !== null && courseDetails.teorik !== undefined && courseDetails.teorik !== '' ? courseDetails.teorik : 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Uygulama:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input type="number" style={{width:'100%'}} value={courseInfoDraft.uygulama !== null && courseInfoDraft.uygulama !== undefined && courseInfoDraft.uygulama !== '' ? courseInfoDraft.uygulama : ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, uygulama: e.target.value})} />
                ) : (courseDetails.uygulama !== null && courseDetails.uygulama !== undefined && courseDetails.uygulama !== '' ? courseDetails.uygulama : 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Laboratuvar:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input type="number" style={{width:'100%'}} value={courseInfoDraft.laboratuvar !== null && courseInfoDraft.laboratuvar !== undefined && courseInfoDraft.laboratuvar !== '' ? courseInfoDraft.laboratuvar : ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, laboratuvar: e.target.value})} />
                ) : (courseDetails.laboratuvar !== null && courseDetails.laboratuvar !== undefined && courseDetails.laboratuvar !== '' ? courseDetails.laboratuvar : 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Kredi:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input type="number" step="0.01" style={{width:'100%'}} value={courseInfoDraft.kredi !== null && courseInfoDraft.kredi !== undefined && courseInfoDraft.kredi !== '' ? courseInfoDraft.kredi : ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, kredi: e.target.value})} />
                ) : (courseDetails.kredi !== null && courseDetails.kredi !== undefined && courseDetails.kredi !== '' ? courseDetails.kredi : 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">AKTS:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input type="number" step="0.01" style={{width:'100%'}} value={courseInfoDraft.akts !== null && courseInfoDraft.akts !== undefined && courseInfoDraft.akts !== '' ? courseInfoDraft.akts : ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, akts: e.target.value})} />
                ) : (courseDetails.akts !== null && courseDetails.akts !== undefined && courseDetails.akts !== '' ? courseDetails.akts : 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">ECTS Kredisi:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input type="number" step="0.01" style={{width:'100%'}} value={courseInfoDraft.ects_credits !== null && courseInfoDraft.ects_credits !== undefined && courseInfoDraft.ects_credits !== '' ? courseInfoDraft.ects_credits : ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, ects_credits: e.target.value})} />
                ) : (courseDetails.ects_credits !== null && courseDetails.ects_credits !== undefined && courseDetails.ects_credits !== '' ? courseDetails.ects_credits : 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Ders Tipi:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.course_type || courseInfoDraft.cd_course_type || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, course_type: e.target.value, cd_course_type: e.target.value})} />
                ) : (courseDetails.course_type || courseDetails.cd_course_type || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Katalog No:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.catalog_no || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, catalog_no: e.target.value})} />
                ) : (courseDetails.catalog_no || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Ders Dili:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.course_language || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, course_language: e.target.value})} />
                ) : (courseDetails.course_language || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Koordinatör:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.coordinator || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, coordinator: e.target.value})} />
                ) : (courseDetails.coordinator || 'Belirtilmemiş')}</td>
              </tr>
              <tr>
                <td className="table-label">Öğretim Üyesi:</td>
                <td className="table-value">{editCourseInfo ? (
                  <input style={{width:'100%'}} value={courseInfoDraft.ogretim_uyesi || ''} onChange={e => setCourseInfoDraft({...courseInfoDraft, ogretim_uyesi: e.target.value})} />
                ) : (courseDetails.ogretim_uyesi || 'Belirtilmemiş')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Dersin Amacı */}
        { (courseDetails.aim || otherInfo?.aim || canEditSyllabus) && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>Dersin Amacı</h3>
              {canEditSyllabus && (
                editAim ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditAim(false); setAimDraft(courseDetails?.aim || otherInfo?.aim || ''); }}>İptal</button>
                    <button className="save-btn" onClick={saveAim}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditAim(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="course-details-table">
              <tbody>
                <tr>
                  <td className="table-value">
                    {editAim ? (
                      <textarea style={{width:'100%', minHeight:'150px'}} value={aimDraft} onChange={e => setAimDraft(e.target.value)} />
                    ) : (
                      <div style={{ whiteSpace: 'pre-line' }}>{courseDetails.aim || otherInfo?.aim}</div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            {renderAiHelper('aim')}
          </div>
        )}

        {/* Dersin İçeriği */}
        { (courseDetails.content || otherInfo?.content || canEditSyllabus) && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>Dersin İçeriği</h3>
              {canEditSyllabus && (
                editContent ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditContent(false); setContentDraft(courseDetails?.content || otherInfo?.content || ''); }}>İptal</button>
                    <button className="save-btn" onClick={saveContent}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditContent(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="course-details-table">
              <tbody>
                <tr>
                  <td className="table-value">
                    {editContent ? (
                      <textarea style={{width:'100%', minHeight:'150px'}} value={contentDraft} onChange={e => setContentDraft(e.target.value)} />
                    ) : (
                      <div style={{ whiteSpace: 'pre-line' }}>{courseDetails.content || otherInfo?.content}</div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            {renderAiHelper('content')}
          </div>
        )}

        {/* Ders Kaynakları ve Önerilen Kaynaklar */}
        { ((courseDetails.course_resources || otherInfo?.course_resources) || (courseDetails.recommended_resources || otherInfo?.recommended_resources)) && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>Ders Kaynakları ve Önerilen Kaynaklar</h3>
              {canEditSyllabus && (
                editResources ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditResources(false); setResourcesDraft({ course_resources: courseDetails?.course_resources || otherInfo?.course_resources || '', recommended_resources: courseDetails?.recommended_resources || otherInfo?.recommended_resources || '' }); }}>İptal</button>
                    <button className="save-btn" onClick={saveResources}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditResources(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="course-details-table">
              <tbody>
                {(courseDetails.course_resources || otherInfo?.course_resources) && (
                  <tr>
                    <td className="table-label">Ders Kaynakları:</td>
                    <td className="table-value">
                      {editResources ? (
                        <textarea style={{width:'100%', minHeight:'100px'}} value={resourcesDraft.course_resources} onChange={e => setResourcesDraft({...resourcesDraft, course_resources: e.target.value})} />
                      ) : (
                        <div style={{ whiteSpace: 'pre-line' }}>{courseDetails.course_resources || otherInfo?.course_resources}</div>
                      )}
                    </td>
                  </tr>
                )}
                {(courseDetails.recommended_resources || otherInfo?.recommended_resources) && (
                  <tr>
                    <td className="table-label">Önerilen Kaynaklar:</td>
                    <td className="table-value">
                      {editResources ? (
                        <textarea style={{width:'100%', minHeight:'100px'}} value={resourcesDraft.recommended_resources} onChange={e => setResourcesDraft({...resourcesDraft, recommended_resources: e.target.value})} />
                      ) : (
                        <div style={{ whiteSpace: 'pre-line' }}>{courseDetails.recommended_resources || otherInfo?.recommended_resources}</div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* İş Yükü Bilgileri */}
        { (courseDetails.total_workload || courseDetails.total_workload_per_30 || courseDetails.akts_credit) && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>İş Yükü Bilgileri</h3>
              {canEditSyllabus && (
                editWorkload ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditWorkload(false); setWorkloadDraft({ total_workload: courseDetails?.total_workload || '', total_workload_per_30: courseDetails?.total_workload_per_30 || '', akts_credit: courseDetails?.akts_credit || '' }); }}>İptal</button>
                    <button className="save-btn" onClick={saveWorkload}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditWorkload(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="course-details-table">
              <tbody>
                <tr>
                  <td className="table-label">Toplam İş Yükü:</td>
                  <td className="table-value">{editWorkload ? (
                    <input type="number" style={{width:'100%'}} value={workloadDraft.total_workload} onChange={e => setWorkloadDraft({...workloadDraft, total_workload: e.target.value})} />
                  ) : (courseDetails.total_workload || '-')}</td>
                </tr>
                <tr>
                  <td className="table-label">30 Saatlik İş Yükü:</td>
                  <td className="table-value">{editWorkload ? (
                    <input style={{width:'100%'}} value={workloadDraft.total_workload_per_30} onChange={e => setWorkloadDraft({...workloadDraft, total_workload_per_30: e.target.value})} />
                  ) : (courseDetails.total_workload_per_30 || '-')}</td>
                </tr>
                <tr>
                  <td className="table-label">AKTS Kredisi:</td>
                  <td className="table-value">{editWorkload ? (
                    <input type="number" style={{width:'100%'}} value={workloadDraft.akts_credit} onChange={e => setWorkloadDraft({...workloadDraft, akts_credit: e.target.value})} />
                  ) : (courseDetails.akts_credit || '-')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* 2. Diğer Bilgiler */}
        {otherInfo && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>Diğer Bilgiler</h3>
              {canEditSyllabus && (
                editOther ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditOther(false); setOtherDraft(otherInfo || {}); }}>İptal</button>
                    <button className="save-btn" onClick={saveOther}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditOther(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="course-details-table">
              <tbody>
                <tr>
                  <td className="table-label">Ön Koşul Dersleri:</td>
                  <td className="table-value">{editOther ? (
                    <input style={{width:'100%'}} value={otherDraft.prerequisite_courses || ''} onChange={e => setOtherDraft({...otherDraft, prerequisite_courses: e.target.value})} />
                  ) : (otherInfo.prerequisite_courses || 'Yok')}</td>
                </tr>
                <tr>
                  <td className="table-label">Özel Koşul:</td>
                  <td className="table-value">{editOther ? (
                    <input style={{width:'100%'}} value={otherDraft.special_condition || ''} onChange={e => setOtherDraft({...otherDraft, special_condition: e.target.value})} />
                  ) : (otherInfo.special_condition || 'Yok')}</td>
                </tr>
                <tr>
                  <td className="table-label">Web Sitesi:</td>
                  <td className="table-value">{editOther ? (
                    <input style={{width:'100%'}} value={otherDraft.website || ''} onChange={e => setOtherDraft({...otherDraft, website: e.target.value})} />
                  ) : (otherInfo.website || '-')}</td>
                </tr>
                <tr>
                  <td className="table-label">Etik Kurallar:</td>
                  <td className="table-value">{editOther ? (
                    <textarea style={{width:'100%'}} value={otherDraft.ethical_rules || ''} onChange={e => setOtherDraft({...otherDraft, ethical_rules: e.target.value})} />
                  ) : (
                    otherInfo.ethical_rules ? (
                      <div style={{ whiteSpace: 'pre-line' }}>{otherInfo.ethical_rules}</div>
                    ) : 'Belirtilmemiş'
                  )}</td>
                </tr>
                <tr>
                  <td className="table-label">Ölçme Değerlendirme Yöntemi:</td>
                  <td className="table-value">{courseDetails.evaluation_method || courseDetails.olcme_degerlendirme_yontemi || otherInfo?.olcme_degerlendirme_yontemi || otherInfo?.assessment_method || 'Belirtilmemiş'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* 3. AKTS Öğrenci İş Yükü Tablosu */}
        {aktsActivities && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>AKTS (Öğrenci İş Yükü) Tablosu</h3>
              {canEditSyllabus && (
                editAkts ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditAkts(false); setAktsDraft(aktsActivities.map(a => ({...a}))); }}>İptal</button>
                    <button className="save-btn" onClick={saveAkts}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditAkts(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="akts-table">
              <thead>
                <tr>
                  <th>Etkinlikler/Faaliyet</th>
                  <th>Sayı</th>
                  <th>Süre (Saat)</th>
                  <th>Toplam (İş Yükü)</th>
                </tr>
              </thead>
              <tbody>
                {editAkts ? (
                  (aktsDraft.length > 0 ? aktsDraft : [{activity_name:'',count:'',duration_hours:'',total_workload:''}]).map((activity, index) => (
                    <tr key={index}>
                      <td><input style={{width:'100%'}} value={activity.activity_name || ''} onChange={e => { const d=[...aktsDraft]; d[index]={...d[index], activity_name:e.target.value}; setAktsDraft(d); }} /></td>
                      <td><input style={{width:'100%'}} value={activity.count || ''} onChange={e => { const d=[...aktsDraft]; d[index]={...d[index], count:e.target.value}; setAktsDraft(d); }} /></td>
                      <td><input style={{width:'100%'}} value={activity.duration_hours || ''} onChange={e => { const d=[...aktsDraft]; d[index]={...d[index], duration_hours:e.target.value}; setAktsDraft(d); }} /></td>
                      <td><input style={{width:'100%'}} value={activity.total_workload || ''} onChange={e => { const d=[...aktsDraft]; d[index]={...d[index], total_workload:e.target.value}; setAktsDraft(d); }} /></td>
                    </tr>
                  ))
                ) : aktsActivities.length > 0 ? (
                  aktsActivities.map((activity, index) => (
                    <tr key={index}>
                      <td>{activity.activity_name}</td>
                      <td>{activity.count}</td>
                      <td>{activity.duration_hours}</td>
                      <td>{activity.total_workload}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{textAlign: 'center', padding: '20px'}}>
                      Veri bulunamadı
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 4. Dersin Dersin öğrenme çıktıları */}
        {learningOutcomes && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3> Dersin Öğrenme Çıktıları</h3>
              {canEditLearningOutcomes && (
                editLearning ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditLearning(false); setLearningDraft((learningOutcomes||[]).map(o => o.name || o.description || '')); }}>İptal</button>
                    <button className="save-btn" onClick={saveLearning}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditLearning(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="learning-outcomes-table">
              <thead>
                <tr>
                  <th>Sayı</th>
                  <th> Dersin Öğrenme Çıktıları</th>
                  <th>Program Çıktıları Eşlemesi</th>
                </tr>
              </thead>
              <tbody>
                {editLearning ? (
                  (learningOutcomes || []).map((outcome, index) => (
                  <tr key={index}>
                    <td style={{whiteSpace:'nowrap', verticalAlign:'top'}}>
                      <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start', gap:'6px'}}>
                        <span style={{fontWeight:'600'}}>{outcome.code || outcome.number || index+1}</span>
                        {(() => {
                          const alignScore = (aiLoAssessments[outcome.id]?.items || [])
                            .map(it => it.alignment_score || 0)
                            .reduce((a, b) => Math.max(a, b), null);
                          const topItem = (aiLoAssessments[outcome.id]?.items || []).find(it => (it.alignment_score || 0) === alignScore);
                          return (
                            <>
                              {alignScore ? (
                                <div className="ai-inline-badge">Uyum: %{alignScore}</div>
                              ) : null}
                              {alignScore !== null && alignScore < 70 && topItem?.suggestion ? (
                                <div className="ai-inline-hint">
                                  <div style={{marginBottom:'6px'}}>{topItem.suggestion}</div>
                                  <button
                                    className="ai-inline-btn"
                                    style={{width:'auto', padding:'6px 12px'}}
                                    onClick={() => {
                                      if (canEditLearningOutcomes) {
                                        applyLoTextSuggestion(outcome.id, topItem.suggestion);
                                      }
                                    }}
                                    disabled={!canEditLearningOutcomes}
                                  >
                                    Öneriyi Uygula
                                  </button>
                                </div>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>
                    </td>
                      <td>
                        <input 
                          style={{width:'100%', fontSize: '14px', padding: '8px'}} 
                          value={learningDraft[index] || ''} 
                          onChange={e => {
                            if (canEditLearningOutcomes) {
                              const d = [...learningDraft]; d[index] = e.target.value; setLearningDraft(d);
                            }
                          }}
                          disabled={!canEditLearningOutcomes}
                        />
                      </td>
                      <td style={{padding: '10px', fontSize: '13px'}}>
                        <Select
                          key={`select-${outcome.id}-${programOutcomes.length}`}
                          isMulti
                          options={programOutcomes.map(po => {
                            const code = po.code || `PÇ${po.number}` || `PÇ${po.id}`;
                            const description = po.description || po.name || '';
                            const label = description ? `${code} - ${description}` : code;
                            return { value: String(po.id), label };
                          })}
                          value={getSelectedProgramOutcomeOptions(outcome)}
                          onChange={selectedOptions => {
                            const selectedValues = selectedOptions ? selectedOptions.map(opt => parseInt(opt.value)) : [];
                            handleMappingChange(outcome.id, selectedValues);
                          }}
                          placeholder="Program Çıktısı Seçin"
                          isDisabled={loadingMapping || !canEditLearningOutcomes}
                          menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                            control: base => ({ 
                              ...base, 
                              minHeight: '38px',
                              fontSize: '13px',
                              borderColor: '#cbd5e1'
                            }),
                            placeholder: base => ({ ...base, fontSize: '13px', color: '#94a3b8' }),
                            multiValue: base => ({ ...base, fontSize: '12px' }),
                            multiValueLabel: base => ({ ...base, fontSize: '12px', padding: '2px 6px' }),
                            multiValueRemove: base => ({ ...base, fontSize: '12px' }),
                            option: base => ({ ...base, fontSize: '13px', padding: '8px 12px' })
                          }}
                        />
                        <div style={{marginTop: '8px'}}>
                          <button
                            type="button"
                            onClick={() => requestAiSuggestions(outcome)}
                            disabled={loadingMapping || (aiSuggestions[outcome.id]?.loading) || !canEditLearningOutcomes}
                            style={{
                              padding: '8px 16px',
                              fontSize: '13px',
                              background: !canEditLearningOutcomes ? '#9ca3af' : '#023b58',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: !canEditLearningOutcomes ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              width: '100%',
                              transition: 'all 0.2s',
                              opacity: !canEditLearningOutcomes ? 0.6 : 1
                            }}
                            onMouseOver={(e) => {
                              if (canEditLearningOutcomes) {
                                e.target.style.background = '#034a6b';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (canEditLearningOutcomes) {
                                e.target.style.background = '#023b58';
                              }
                            }}
                          >
                            {aiSuggestions[outcome.id]?.loading ? 'Yapay Zeka analiz ediyor...' : 'Yapay Zeka Eşleştirme Önerisi Al'}
                          </button>
                        </div>
                        {(aiSuggestions[outcome.id]?.suggestions?.length > 0 || aiSuggestions[outcome.id]?.error) && (
                          <div style={{
                            marginTop: '10px',
                            padding: '12px',
                            background: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            maxHeight: expandedAiSuggestions[outcome.id] ? '600px' : 'auto',
                            overflow: expandedAiSuggestions[outcome.id] ? 'auto' : 'hidden'
                          }}>
                            <div 
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                marginBottom: expandedAiSuggestions[outcome.id] ? '10px' : '0'
                              }}
                              onClick={() => setExpandedAiSuggestions(prev => ({
                                ...prev,
                                [outcome.id]: !prev[outcome.id]
                              }))}
                            >
                              <div style={{fontWeight: '600', fontSize: '13px', color: '#0f172a'}}>
                                🤖 AI Önerileri {aiSuggestions[outcome.id]?.suggestions?.length > 0 && `(${aiSuggestions[outcome.id].suggestions.length} öneri)`}
                              </div>
                              <span style={{fontSize: '12px', color: '#64748b'}}>
                                {expandedAiSuggestions[outcome.id] ? '▼' : '▶'}
                              </span>
                            </div>
                            {expandedAiSuggestions[outcome.id] && (
                              <>
                                {aiSuggestions[outcome.id]?.error && (
                                  <div style={{
                                    padding: '10px',
                                    background: '#fee2e2',
                                    borderRadius: '6px',
                                    color: '#dc2626',
                                    fontSize: '12px',
                                    marginBottom: '10px'
                                  }}>
                                    ❌ {aiSuggestions[outcome.id].error}
                                  </div>
                                )}
                                {aiSuggestions[outcome.id]?.suggestions?.length > 0 && (
                                  <>
                                    <div style={{
                                      marginBottom: '10px',
                                      padding: '8px',
                                      background: '#eff6ff',
                                      borderRadius: '6px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}>
                                      <span style={{fontSize: '12px', color: '#1e40af', fontWeight: '500'}}>
                                        {aiSuggestions[outcome.id].suggestions.length} öneri bulundu
                                      </span>
                                      <button
                                        onClick={async () => {
                                          if (!canEditLearningOutcomes) return;
                                          try {
                                            // State'ten güncel veriyi al
                                            const currentOutcome = learningOutcomes.find(lo => lo.id === outcome.id);
                                            const currentIds = normalizeProgramOutcomeIds(currentOutcome?.program_outcome_ids || []);
                                            
                                            // Tüm suggestion ID'lerini al ve normalize et
                                            const allSuggestionIds = aiSuggestions[outcome.id].suggestions
                                              .map(s => s.program_outcome_id)
                                              .filter(id => id != null);
                                            
                                            const normalizedSuggestionIds = normalizeProgramOutcomeIds(allSuggestionIds);
                                            
                                            // Zaten ekli olanları filtrele
                                            const newIds = normalizedSuggestionIds.filter(id => !currentIds.includes(id));
                                            
                                            if (newIds.length > 0) {
                                              // Mevcut ID'ler ile yeni ID'leri birleştir ve normalize et (duplicate'leri kaldır)
                                              const updatedIds = normalizeProgramOutcomeIds([...currentIds, ...newIds]);
                                              console.log('[DEBUG] Tümünü Ekle:', {
                                                currentIds,
                                                normalizedSuggestionIds,
                                                newIds,
                                                updatedIds,
                                                suggestionCount: aiSuggestions[outcome.id].suggestions.length
                                              });
                                              await handleMappingChange(outcome.id, updatedIds);
                                            } else {
                                              console.log('[DEBUG] Tümünü Ekle: Yeni ID yok, zaten ekli', { currentIds, normalizedSuggestionIds });
                                            }
                                            setExpandedAiSuggestions(prev => ({ ...prev, [outcome.id]: false }));
                                          } catch (err) {
                                            console.error('[DEBUG] Tümünü Ekle hatası:', err);
                                            alert('Tümünü eklerken bir hata oluştu: ' + err.message);
                                          }
                                        }}
                                        disabled={!canEditLearningOutcomes}
                                        style={{
                                          padding: '6px 12px',
                                          fontSize: '11px',
                                          background: !canEditLearningOutcomes ? '#9ca3af' : '#10b981',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: !canEditLearningOutcomes ? 'not-allowed' : 'pointer',
                                          fontWeight: '600',
                                          opacity: !canEditLearningOutcomes ? 0.6 : 1
                                        }}
                                      >
                                        ✓ Tümünü Ekle
                                      </button>
                                    </div>
                                    {aiSuggestions[outcome.id].suggestions
                                      .sort((a, b) => (b.confidence || 0) - (a.confidence || 0))
                                      .map((suggestion, idx) => {
                                const isSelected = (outcome.program_outcome_ids || []).includes(parseInt(suggestion.program_outcome_id));
                                const confidencePercent = Math.round((suggestion.confidence || 0) * 100);
                                const confidenceColor = confidencePercent >= 70 ? '#10b981' : confidencePercent >= 45 ? '#f59e0b' : '#ef4444';
                                
                                return (
                                  <div key={idx} style={{
                                    marginBottom: '8px',
                                    padding: '10px',
                                    background: '#f8fafc',
                                    borderRadius: '6px',
                                    border: `1px solid ${confidenceColor}40`
                                  }}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
                                      <span style={{fontWeight: '600', fontSize: '13px', color: '#0f172a'}}>
                                        {suggestion.program_outcome_code}
                                      </span>
                                      <span style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        background: `${confidenceColor}20`,
                                        color: confidenceColor,
                                        fontWeight: '700',
                                        fontSize: '12px'
                                      }}>
                                        %{confidencePercent} Uyum
                                      </span>
                                    </div>
                                    {suggestion.match_reason && (
                                      <div style={{fontSize: '11px', color: '#64748b', marginBottom: '5px', fontStyle: 'italic', lineHeight: '1.4'}}>
                                        {suggestion.match_reason}
                                      </div>
                                    )}
                                    {!isSelected && (
                                      <button
                                        onClick={() => {
                                          if (canEditLearningOutcomes) {
                                            applyAISuggestion(outcome.id, suggestion);
                                          }
                                        }}
                                        disabled={!canEditLearningOutcomes}
                                        style={{
                                          marginTop: '5px',
                                          padding: '4px 12px',
                                          fontSize: '11px',
                                          background: !canEditLearningOutcomes ? '#9ca3af' : '#2563eb',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: !canEditLearningOutcomes ? 'not-allowed' : 'pointer',
                                          fontWeight: '500',
                                          opacity: !canEditLearningOutcomes ? 0.6 : 1
                                        }}
                                      >
                                        Ekle
                                      </button>
                                    )}
                                    {isSelected && (
                                      <span style={{color: '#10b981', fontSize: '11px', fontWeight: '600', display: 'inline-block', marginTop: '5px'}}>
                                        ✓ Eklendi
                                      </span>
                                    )}
                                  </div>
                                      );
                                    })}
                                    {aiSuggestions[outcome.id]?.overallReasoning && (
                                      <div style={{
                                        marginTop: '10px',
                                        padding: '10px',
                                        background: '#eff6ff',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        color: '#1e40af',
                                        borderLeft: '3px solid #2563eb',
                                        lineHeight: '1.5'
                                      }}>
                                        <strong>Genel Analiz:</strong> {aiSuggestions[outcome.id].overallReasoning}
                                      </div>
                                    )}
                                    {(aiLoAssessments[outcome.id]?.items?.length > 0 || aiLoAssessments[outcome.id]?.error) && (
                                      <div style={{
                                        marginTop: '10px',
                                        padding: '10px',
                                        background: '#fefce8',
                                        borderRadius: '8px',
                                        border: '1px solid #facc15'
                                      }}>
                                        <div style={{fontWeight:'700', fontSize:'13px', color:'#854d0e', marginBottom:'6px'}}>
                                          🎯 Uyum Analizi
                                        </div>
                                        {aiLoAssessments[outcome.id]?.error && (
                                          <div style={{color:'#b91c1c', fontSize:'12px', marginBottom:'6px'}}>
                                            ❌ {aiLoAssessments[outcome.id].error}
                                          </div>
                                        )}
                                      {(aiLoAssessments[outcome.id]?.items || []).map((it, i) => (
                                        <div key={i} style={{marginBottom:'8px', fontSize:'12px', color:'#713f12'}}>
                                          <div style={{display:'flex', justifyContent:'space-between', gap:'8px', alignItems:'center'}}>
                                            <span><strong>Uyum:</strong> %{it.alignment_score ?? '-'}</span>
                                            {it.suggestion && (
                                              <button
                                                className="ai-inline-btn"
                                                style={{width:'auto', padding:'5px 10px'}}
                                                onClick={() => {
                                                  if (canEditLearningOutcomes) {
                                                    applyLoTextSuggestion(outcome.id, it.suggestion);
                                                  }
                                                }}
                                                disabled={!canEditLearningOutcomes}
                                              >
                                                Metni uygula
                                              </button>
                                            )}
                                          </div>
                                          {it.suggestion && <div style={{marginTop:'4px'}}>{it.suggestion}</div>}
                                        </div>
                                      ))}
                                        {aiLoAssessments[outcome.id]?.overallReasoning && (
                                          <div style={{
                                            marginTop:'6px',
                                            padding:'6px',
                                            background:'#fff8e1',
                                            borderRadius:'6px',
                                            border:'1px solid #fde68a',
                                            fontSize:'12px',
                                            color:'#92400e'
                                          }}>
                                            <strong>Genel:</strong> {aiLoAssessments[outcome.id].overallReasoning}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : learningOutcomes.length > 0 ? (
                  learningOutcomes.map((outcome, index) => {
                    const aiState = aiSuggestions[outcome.id] || {};
                    return (
                      <tr key={index}>
                        <td style={{whiteSpace:'nowrap', verticalAlign:'top'}}>
                          <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start', gap:'6px'}}>
                            <span style={{fontWeight:'600'}}>{outcome.code || outcome.number}</span>
                            {(() => {
                              const alignScore = (aiLoAssessments[outcome.id]?.items || [])
                                .map(it => it.alignment_score || 0)
                                .reduce((a, b) => Math.max(a, b), null);
                              const topItem = (aiLoAssessments[outcome.id]?.items || []).find(it => (it.alignment_score || 0) === alignScore);
                              return (
                                <>
                                  {alignScore ? (
                                    <div className="ai-inline-badge">Uyum: %{alignScore}</div>
                                  ) : null}
                                  {alignScore !== null && alignScore < 70 && topItem?.suggestion ? (
                                    <div className="ai-inline-hint">
                                      <div style={{marginBottom:'6px'}}>{topItem.suggestion}</div>
                                      <button
                                        className="ai-inline-btn"
                                        style={{width:'auto', padding:'6px 12px'}}
                                        onClick={() => {
                                          if (canEditLearningOutcomes) {
                                            applyLoTextSuggestion(outcome.id, topItem.suggestion);
                                          }
                                        }}
                                        disabled={!canEditLearningOutcomes}
                                      >
                                        Öneriyi Uygula
                                      </button>
                                    </div>
                                  ) : null}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                        <td style={{fontSize: '14px', lineHeight: '1.6'}}>{outcome.name || outcome.description}</td>
                        <td style={{padding: '10px', fontSize: '13px'}}>
                          <Select
                            key={`select-view-${outcome.id}-${programOutcomes.length}`}
                            isMulti
                            options={programOutcomes.map(po => {
                              const code = po.code || `PÇ${po.number}` || `PÇ${po.id}`;
                              const description = po.description || po.name || '';
                              const label = description ? `${code} - ${description}` : code;
                              return { value: String(po.id), label };
                            })}
                          value={getSelectedProgramOutcomeOptions(outcome)}
                            onChange={selectedOptions => {
                              const selectedValues = selectedOptions ? selectedOptions.map(opt => parseInt(opt.value)) : [];
                              handleMappingChange(outcome.id, selectedValues);
                            }}
                            placeholder="Program Çıktısı Seçin"
                            isDisabled={loadingMapping || !canEditLearningOutcomes}
                            menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                            styles={{
                              menuPortal: base => ({ ...base, zIndex: 9999 }),
                              control: base => ({ 
                                ...base, 
                                minHeight: '38px',
                                fontSize: '13px',
                                borderColor: '#cbd5e1'
                              }),
                              placeholder: base => ({ ...base, fontSize: '13px', color: '#94a3b8' }),
                              multiValue: base => ({ ...base, fontSize: '12px' }),
                              multiValueLabel: base => ({ ...base, fontSize: '12px', padding: '2px 6px' }),
                              multiValueRemove: base => ({ ...base, fontSize: '12px' }),
                              option: base => ({ ...base, fontSize: '13px', padding: '8px 12px' })
                            }}
                          />
                          <div style={{marginTop: '8px'}}>
                            <button
                              type="button"
                              onClick={() => requestAiSuggestions(outcome)}
                              disabled={loadingMapping || aiState.loading || !canEditLearningOutcomes}
                              style={{
                                padding: '8px 16px',
                                fontSize: '13px',
                                background: !canEditLearningOutcomes ? '#9ca3af' : '#023b58',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: !canEditLearningOutcomes ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                width: '100%',
                                transition: 'all 0.2s',
                                opacity: !canEditLearningOutcomes ? 0.6 : 1
                              }}
                              onMouseOver={(e) => {
                                if (canEditLearningOutcomes) {
                                  e.target.style.background = '#034a6b';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (canEditLearningOutcomes) {
                                  e.target.style.background = '#023b58';
                                }
                              }}
                            >
                              {aiState.loading ? 'Yapay Zeka analiz ediyor...' : 'Yapay Zeka Eşleştirme Önerisi Al'}
                            </button>
                          </div>
                          {(aiState.suggestions?.length > 0 || aiState.error) && (
                            <div style={{
                              marginTop: '10px',
                              padding: '12px',
                              background: '#f8fafc',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              maxHeight: expandedAiSuggestions[outcome.id] ? '600px' : 'auto',
                              overflow: expandedAiSuggestions[outcome.id] ? 'auto' : 'hidden'
                            }}>
                              <div 
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  marginBottom: expandedAiSuggestions[outcome.id] ? '10px' : '0'
                                }}
                                onClick={() => setExpandedAiSuggestions(prev => ({
                                  ...prev,
                                  [outcome.id]: !prev[outcome.id]
                                }))}
                              >
                                <div style={{fontWeight: '600', fontSize: '13px', color: '#0f172a'}}>
                                  🤖 AI Önerileri {aiState.suggestions?.length > 0 && `(${aiState.suggestions.length} öneri)`}
                                </div>
                                <span style={{fontSize: '12px', color: '#64748b'}}>
                                  {expandedAiSuggestions[outcome.id] ? '▼' : '▶'}
                                </span>
                              </div>
                              {expandedAiSuggestions[outcome.id] && (
                                <>
                                  {aiState.error && (
                                    <div style={{
                                      padding: '10px',
                                      background: '#fee2e2',
                                      borderRadius: '6px',
                                      color: '#dc2626',
                                      fontSize: '12px',
                                      marginBottom: '10px'
                                    }}>
                                      ❌ {aiState.error}
                                    </div>
                                  )}
                                  {aiState.suggestions?.length > 0 && (
                                    <>
                                      <div style={{
                                        marginBottom: '10px',
                                        padding: '8px',
                                        background: '#eff6ff',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                      }}>
                                        <span style={{fontSize: '12px', color: '#1e40af', fontWeight: '500'}}>
                                          {aiState.suggestions.length} öneri bulundu
                                        </span>
                                        <button
                                          onClick={async () => {
                                            if (!canEditLearningOutcomes) return;
                                            try {
                                              // State'ten güncel veriyi al
                                              const currentOutcome = learningOutcomes.find(lo => lo.id === outcome.id);
                                              const currentIds = normalizeProgramOutcomeIds(currentOutcome?.program_outcome_ids || []);
                                              
                                              // Tüm suggestion ID'lerini al ve normalize et
                                              const allSuggestionIds = aiState.suggestions
                                                .map(s => s.program_outcome_id)
                                                .filter(id => id != null);
                                              
                                              const normalizedSuggestionIds = normalizeProgramOutcomeIds(allSuggestionIds);
                                              
                                              // Zaten ekli olanları filtrele
                                              const newIds = normalizedSuggestionIds.filter(id => !currentIds.includes(id));
                                              
                                              if (newIds.length > 0) {
                                                // Mevcut ID'ler ile yeni ID'leri birleştir ve normalize et (duplicate'leri kaldır)
                                                const updatedIds = normalizeProgramOutcomeIds([...currentIds, ...newIds]);
                                                console.log('[DEBUG] Tümünü Ekle (view):', {
                                                  currentIds,
                                                  normalizedSuggestionIds,
                                                  newIds,
                                                  updatedIds,
                                                  suggestionCount: aiState.suggestions.length
                                                });
                                                await handleMappingChange(outcome.id, updatedIds);
                                              } else {
                                                console.log('[DEBUG] Tümünü Ekle (view): Yeni ID yok, zaten ekli', { currentIds, normalizedSuggestionIds });
                                              }
                                              setExpandedAiSuggestions(prev => ({ ...prev, [outcome.id]: false }));
                                            } catch (err) {
                                              console.error('[DEBUG] Tümünü Ekle (view) hatası:', err);
                                              alert('Tümünü eklerken bir hata oluştu: ' + err.message);
                                            }
                                          }}
                                          disabled={!canEditLearningOutcomes}
                                          style={{
                                            padding: '6px 12px',
                                            fontSize: '11px',
                                            background: !canEditLearningOutcomes ? '#9ca3af' : '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: !canEditLearningOutcomes ? 'not-allowed' : 'pointer',
                                            fontWeight: '600',
                                            opacity: !canEditLearningOutcomes ? 0.6 : 1
                                          }}
                                        >
                                          ✓ Tümünü Ekle
                                        </button>
                                      </div>
                                      {aiState.suggestions
                                        .sort((a, b) => (b.confidence || 0) - (a.confidence || 0))
                                        .map((suggestion, idx) => {
                                      const isSelected = (outcome.program_outcome_ids || []).includes(parseInt(suggestion.program_outcome_id));
                                      const confidencePercent = Math.round((suggestion.confidence || 0) * 100);
                                      const confidenceColor = confidencePercent >= 70 ? '#10b981' : confidencePercent >= 45 ? '#f59e0b' : '#ef4444';
                                      
                                      return (
                                        <div key={idx} style={{
                                          marginBottom: '8px',
                                          padding: '10px',
                                          background: '#f8fafc',
                                          borderRadius: '6px',
                                          border: `1px solid ${confidenceColor}40`
                                        }}>
                                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
                                            <span style={{fontWeight: '600', fontSize: '13px', color: '#0f172a'}}>
                                              {suggestion.program_outcome_code}
                                            </span>
                                            <span style={{
                                              padding: '4px 8px',
                                              borderRadius: '4px',
                                              background: `${confidenceColor}20`,
                                              color: confidenceColor,
                                              fontWeight: '700',
                                              fontSize: '12px'
                                            }}>
                                              %{confidencePercent} Uyum
                                            </span>
                                          </div>
                                          {suggestion.match_reason && (
                                            <div style={{fontSize: '11px', color: '#64748b', marginBottom: '5px', fontStyle: 'italic', lineHeight: '1.4'}}>
                                              {suggestion.match_reason}
                                            </div>
                                          )}
                                          {!isSelected && (
                                            <button
                                              onClick={() => {
                                                if (canEditLearningOutcomes) {
                                                  applyAISuggestion(outcome.id, suggestion);
                                                }
                                              }}
                                              disabled={!canEditLearningOutcomes}
                                              style={{
                                                marginTop: '5px',
                                                padding: '4px 12px',
                                                fontSize: '11px',
                                                background: !canEditLearningOutcomes ? '#9ca3af' : '#2563eb',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: !canEditLearningOutcomes ? 'not-allowed' : 'pointer',
                                                fontWeight: '500',
                                                opacity: !canEditLearningOutcomes ? 0.6 : 1
                                              }}
                                            >
                                              Ekle
                                            </button>
                                          )}
                                          {isSelected && (
                                            <span style={{color: '#10b981', fontSize: '11px', fontWeight: '600', display: 'inline-block', marginTop: '5px'}}>
                                              ✓ Eklendi
                                            </span>
                                          )}
                                        </div>
                                        );
                                      })}
                                      {aiState.overallReasoning && (
                                        <div style={{
                                          marginTop: '10px',
                                          padding: '10px',
                                          background: '#eff6ff',
                                          borderRadius: '6px',
                                          fontSize: '11px',
                                          color: '#1e40af',
                                          borderLeft: '3px solid #2563eb',
                                          lineHeight: '1.5'
                                        }}>
                                          <strong>Genel Analiz:</strong> {aiState.overallReasoning}
                                        </div>
                                      )}
                                      {(aiLoAssessments[outcome.id]?.items?.length > 0 || aiLoAssessments[outcome.id]?.error) && (
                                        <div style={{
                                          marginTop: '10px',
                                          padding: '10px',
                                          background: '#fefce8',
                                          borderRadius: '8px',
                                          border: '1px solid #facc15'
                                        }}>
                                          <div style={{fontWeight:'700', fontSize:'13px', color:'#854d0e', marginBottom:'6px'}}>
                                            🎯 Uyum Analizi
                                          </div>
                                          {aiLoAssessments[outcome.id]?.error && (
                                            <div style={{color:'#b91c1c', fontSize:'12px', marginBottom:'6px'}}>
                                              ❌ {aiLoAssessments[outcome.id].error}
                                            </div>
                                          )}
                                          {(aiLoAssessments[outcome.id]?.items || []).map((it, i) => (
                                            <div key={i} style={{marginBottom:'6px', fontSize:'12px', color:'#713f12'}}>
                                              <div><strong>Uyum:</strong> %{it.alignment_score ?? '-'}</div>
                                              {it.suggestion && <div style={{marginTop:'2px'}}>{it.suggestion}</div>}
                                            </div>
                                          ))}
                                          {aiLoAssessments[outcome.id]?.overallReasoning && (
                                            <div style={{
                                              marginTop:'6px',
                                              padding:'6px',
                                              background:'#fff8e1',
                                              borderRadius:'6px',
                                              border:'1px solid #fde68a',
                                              fontSize:'12px',
                                              color:'#92400e'
                                            }}>
                                              <strong>Genel:</strong> {aiLoAssessments[outcome.id].overallReasoning}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" style={{textAlign: 'center', padding: '20px'}}>
                      Veri bulunamadı
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 5. Haftalara Göre İşlenecek Konular */}
        {weeklyTopics && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>Haftalara Göre İşlenecek Konular</h3>
              {canEditSyllabus && (
                editWeekly ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditWeekly(false); setWeeklyDraft((weeklyTopics||[]).map(t => ({ topic: t.topic || '', on_hazirlik: t.on_hazirlik || '', metodoloji: t.metodoloji || '' }))); }}>İptal</button>
                    <button className="save-btn" onClick={saveWeekly}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditWeekly(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="weekly-topics-table">
              <thead>
                <tr>
                  <th>Hafta</th>
                  <th>Konu</th>
                  <th>Ön Hazırlık</th>
                  <th>Metodoloji</th>
                  <th>Ders Notları</th>
                </tr>
              </thead>
              <tbody>
                {editWeekly ? (
                  (weeklyTopics || []).map((topic, index) => (
                    <tr key={index}>
                      <td style={{textAlign: 'center', width: '60px'}}>{topic.number}</td>
                      <td>
                        <input style={{width:'100%'}} value={weeklyDraft[index]?.topic || ''} onChange={e => {
                          const d = [...weeklyDraft];
                          d[index] = {...d[index], topic: e.target.value};
                          setWeeklyDraft(d);
                        }} />
                      </td>
                      <td>
                        <input style={{width:'100%'}} value={weeklyDraft[index]?.on_hazirlik || ''} onChange={e => {
                          const d = [...weeklyDraft];
                          d[index] = {...d[index], on_hazirlik: e.target.value};
                          setWeeklyDraft(d);
                        }} />
                      </td>
                      <td>
                        <input style={{width:'100%'}} value={weeklyDraft[index]?.metodoloji || ''} onChange={e => {
                          const d = [...weeklyDraft];
                          d[index] = {...d[index], metodoloji: e.target.value};
                          setWeeklyDraft(d);
                        }} />
                      </td>
                      <td style={{textAlign: 'center', width: '80px'}}>
                        <span 
                          className="course-notes-icon" 
                          title="Ders Notlarını Görüntüle"
                          onClick={() => openWeekModal(topic.number)}
                          style={{ cursor: 'pointer', fontSize: '18px' }}
                        >
                          📁
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  weeklyTopics.map((topic, index) => (
                    <tr key={index}>
                      <td style={{textAlign: 'center', width: '60px'}}>{topic.number}</td>
                      <td>{topic.topic}</td>
                      <td>{topic.on_hazirlik || '-'}</td>
                      <td>{topic.metodoloji || '-'}</td>
                      <td style={{textAlign: 'center', width: '80px'}}>
                        <span 
                          className="course-notes-icon" 
                          title="Ders Notlarını Görüntüle"
                          onClick={() => openWeekModal(topic.number)}
                          style={{ cursor: 'pointer', fontSize: '18px' }}
                        >
                          📁
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 6. Değerlendirme Sistemi */}
        {evaluationSystem && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>Değerlendirme Sistemi</h3>
              {canEditSyllabus && (
                editEval ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditEval(false); setEvalDraft(evaluationSystem.map(a => ({...a}))); }}>İptal</button>
                    <button className="save-btn" onClick={saveEval}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditEval(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="evaluation-table">
              <thead>
                <tr>
                  <th>Etkinlik Adı</th>
                  <th>Sayı</th>
                  <th>Katkı Payı</th>
                </tr>
              </thead>
              <tbody>
                {editEval ? (
                  (evalDraft.length > 0 ? evalDraft : [{activity_name:'',count:'',contribution_share:''}]).map((evaluation, index) => (
                    <tr key={index}>
                      <td><input style={{width:'100%'}} value={evaluation.activity_name || ''} onChange={e => { const d=[...evalDraft]; d[index]={...d[index], activity_name:e.target.value}; setEvalDraft(d); }} /></td>
                      <td style={{textAlign: 'center'}}><input style={{width:'100%'}} value={evaluation.count || ''} onChange={e => { const d=[...evalDraft]; d[index]={...d[index], count:e.target.value}; setEvalDraft(d); }} /></td>
                      <td style={{textAlign: 'center'}}><input style={{width:'100%'}} value={evaluation.contribution_share || ''} onChange={e => { const d=[...evalDraft]; d[index]={...d[index], contribution_share:e.target.value}; setEvalDraft(d); }} /></td>
                    </tr>
                  ))
                ) : (
                  evaluationSystem.map((evaluation, index) => (
                    <tr key={index}>
                      <td>{evaluation.activity_name}</td>
                      <td style={{textAlign: 'center'}}>{evaluation.count}</td>
                      <td style={{textAlign: 'center'}}>{evaluation.contribution_share}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 7. Öğretim Elemanları */}
        {teachingStaff && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>Öğretim Eleman(ları)</h3>
              {canEditSyllabus && (
                editStaff ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditStaff(false); setStaffDraft(teachingStaff.map(s => ({...s}))); }}>İptal</button>
                    <button className="save-btn" onClick={saveStaff}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => setEditStaff(true)}>Düzenle</button>
                )
              )}
            </div>
            <table className="teaching-staff-table">
              <thead>
                <tr>
                  <th>Unvan</th>
                  <th>Ad</th>
                  <th>Soyadı</th>
                  <th>Web Adresi</th>
                  <th>E-Posta</th>
                  <th>Görevi</th>
                </tr>
              </thead>
              <tbody>
                {editStaff ? (
                  (staffDraft.length > 0 ? staffDraft : [{title:'',first_name:'',last_name:'',web_address:'',email:'',duty:''}]).map((staff, index) => (
                    <tr key={index}>
                      <td><input style={{width:'100%'}} value={staff.title || ''} onChange={e => { const d=[...staffDraft]; d[index]={...d[index], title:e.target.value}; setStaffDraft(d); }} /></td>
                      <td><input style={{width:'100%'}} value={staff.first_name || ''} onChange={e => { const d=[...staffDraft]; d[index]={...d[index], first_name:e.target.value}; setStaffDraft(d); }} /></td>
                      <td><input style={{width:'100%'}} value={staff.last_name || ''} onChange={e => { const d=[...staffDraft]; d[index]={...d[index], last_name:e.target.value}; setStaffDraft(d); }} /></td>
                      <td><input style={{width:'100%'}} value={staff.web_address || ''} onChange={e => { const d=[...staffDraft]; d[index]={...d[index], web_address:e.target.value}; setStaffDraft(d); }} /></td>
                      <td><input style={{width:'100%'}} value={staff.email || ''} onChange={e => { const d=[...staffDraft]; d[index]={...d[index], email:e.target.value}; setStaffDraft(d); }} /></td>
                      <td><input style={{width:'100%'}} value={staff.duty || ''} onChange={e => { const d=[...staffDraft]; d[index]={...d[index], duty:e.target.value}; setStaffDraft(d); }} /></td>
                    </tr>
                  ))
                ) : (
                  teachingStaff.map((staff, index) => (
                    <tr key={index}>
                      <td>{staff.title || '-'}</td>
                      <td>{staff.first_name || '-'}</td>
                      <td>{staff.last_name || '-'}</td>
                      <td>{staff.web_address || '-'}</td>
                      <td>{staff.email || '-'}</td>
                      <td>{staff.duty || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 8. Program Öğrenme Çıktıları */}
        <div className="course-details-section">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <h3>Program Öğrenme Çıktıları</h3>
            {canEditSyllabus && (
              editProgram ? (
                <div className="button-group">
                  <button className="cancel-btn" onClick={() => { setEditProgram(false); setProgramDraft((programLearningOutcomes||[]).map(o => o.outcome_description || o.description || '')); setProgramLevelsDraft((programLearningOutcomes || []).map(o => [...(o.contribution_levels || [])])); }}>İptal</button>
                  <button className="save-btn" onClick={saveProgram}>Kaydet</button>
                </div>
              ) : (
                <button className="edit-btn" onClick={() => { setEditProgram(true); setProgramLevelsDraft((programLearningOutcomes || []).map(o => [...(o.contribution_levels || [])])); }}>Düzenle</button>
              )
            )}
          </div>
          <table className="program-outcomes-table">
            <thead>
              <tr>
                <th>Sıra No</th>
                <th>Program Öğrenme Çıktıları</th>
                <th>
                  <div>Katkı Düzeyi</div>
                  <div className="contribution-header">
                    <span>1</span>
                    <span>2</span>
                    <span>3</span>
                    <span>4</span>
                    <span>5</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {editProgram ? (
                (programLearningOutcomes && programLearningOutcomes.length > 0) ? (
                  programLearningOutcomes.map((outcome, index) => (
                    <tr key={index}>
                      <td style={{textAlign: 'center', width: '60px'}}>{outcome.outcome_number || outcome.number || ''}</td>
                      <td>
                        <input style={{width:'100%'}} value={programDraft[index] || ''} onChange={e => {
                          const d = [...programDraft]; d[index] = e.target.value; setProgramDraft(d);
                        }} />
                      </td>
                      <td style={{textAlign: 'center', width: '80px'}}>
                        <div className="contribution-levels">
                          {[1, 2, 3, 4, 5].map((level) => (
                            <div
                              key={level}
                              className={`contribution-level ${(programLevelsDraft[index] || []).includes(level) ? 'active' : ''}`}
                              onClick={() => editProgram && canEditSyllabus && toggleProgramLevel(index, level)}
                              style={{cursor: editProgram && canEditSyllabus ? 'pointer' : 'default'}}
                            >
                              {(programLevelsDraft[index] || []).includes(level) ? 'X' : ''}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{textAlign: 'center', padding: '20px'}}>
                      Veri bulunamadı
                    </td>
                  </tr>
                )
              ) : (
                (programLearningOutcomes && programLearningOutcomes.length > 0) ? (
                  programLearningOutcomes.map((outcome, index) => (
                    <tr key={index}>
                      <td style={{textAlign: 'center', width: '60px'}}>{outcome.outcome_number || outcome.number || ''}</td>
                      <td>{outcome.outcome_description || outcome.description || outcome.name || ''}</td>
                      <td style={{textAlign: 'center', width: '80px'}}>
                        <div className="contribution-levels">
                          {[1, 2, 3, 4, 5].map((level) => (
                            <div
                              key={level}
                              className={`contribution-level ${(programLearningOutcomes[index]?.contribution_levels || []).includes(level) ? 'active' : ''}`}
                            >
                              {(programLearningOutcomes[index]?.contribution_levels || []).includes(level) ? 'X' : ''}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{textAlign: 'center', padding: '20px'}}>
                      Veri bulunamadı
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        {/* 9. Sürdürülebilir Kalkınma Amaçları */}
        {sustainableDevelopmentGoals && (
          <div className="course-details-section">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3>Sürdürülebilir Kalkınma Amaçları</h3>
              {canEditSyllabus && (
                editSDG ? (
                  <div className="button-group">
                    <button className="cancel-btn" onClick={() => { setEditSDG(false); setSdgLevelsDraft((sustainableDevelopmentGoals || []).map(g => [...(g.contribution_levels || [])])); setSdgDraft((sustainableDevelopmentGoals || []).map(g => g.goal_description || '')); }}>İptal</button>
                    <button className="save-btn" onClick={saveSDG}>Kaydet</button>
                  </div>
                ) : (
                  <button className="edit-btn" onClick={() => { setEditSDG(true); setSdgLevelsDraft((sustainableDevelopmentGoals || []).map(g => [...(g.contribution_levels || [])])); setSdgDraft((sustainableDevelopmentGoals || []).map(g => g.goal_description || '')); }}>Düzenle</button>
                )
              )}
            </div>
            <table className="sdg-table">
              <thead>
                <tr>
                  <th>Sıra No</th>
                  <th>Sürdürülebilir Kalkınma Amaçları</th>
                  <th>
                    <div>Katkı Düzeyi</div>
                    <div className="contribution-header">
                      <span>1</span>
                      <span>2</span>
                      <span>3</span>
                      <span>4</span>
                      <span>5</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sustainableDevelopmentGoals
                  .sort((a, b) => parseInt(a.goal_number) - parseInt(b.goal_number))
                  .map((goal, index) => {
                    const origIndex = sustainableDevelopmentGoals.indexOf(goal);
                    return (
                      <tr key={index}>
                        <td style={{textAlign: 'center', width: '40px'}}>{goal.goal_number}</td>
                        <td>
                          {editSDG ? (
                            <input 
                              style={{width:'100%'}} 
                              value={sdgDraft[origIndex] || ''} 
                              onChange={e => {
                                const d = [...sdgDraft];
                                d[origIndex] = e.target.value;
                                setSdgDraft(d);
                              }} 
                            />
                          ) : (
                            goal.goal_description
                          )}
                        </td>
                        <td style={{textAlign: 'center', width: '80px'}}>
                          <div className="contribution-levels">
                            {[1, 2, 3, 4, 5].map((level) => (
                              <div
                                key={level}
                                className={`contribution-level ${editSDG ? ((sdgLevelsDraft[origIndex] || []).includes(level) ? 'active' : '') : ((goal.contribution_levels || []).includes(level) ? 'active' : '')}`}
                                onClick={() => editSDG && canEditSyllabus && toggleSDGLevel(origIndex, level)}
                                style={{cursor: editSDG && canEditSyllabus ? 'pointer' : 'default'}}
                              >
                                {editSDG ? ((sdgLevelsDraft[origIndex] || []).includes(level) ? 'X' : '') : ((goal.contribution_levels || []).includes(level) ? 'X' : '')}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>

      {/* Haftalık Notlar Modal */}
      {isWeekModalOpen && selectedWeekNumber !== null && (
        <div className="week-modal-overlay" onClick={closeWeekModal}>
          <div className="week-modal" onClick={(e) => e.stopPropagation()}>
            <div className="week-modal-header">
              <h4>{selectedWeekNumber}. Hafta - Ders Notları</h4>
              <button type="button" className="week-modal-close" onClick={closeWeekModal}>✕</button>
            </div>
            <div className="week-modal-content">
              <div className="week-modal-section">
                <h5>Dosyalar</h5>
                {(userType === 'admin' || userType === 'akademisyen') && !isReadOnly() && (
                  <div style={{ marginBottom: '12px' }}>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                      multiple
                      onChange={(e) => {
                        if (isReadOnly()) { e.target.value = ''; return; }
                        const files = Array.from(e.target.files || []);
                        const allowedExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx'];
                        const validFiles = files.filter(file => {
                          const ext = (file.name.split('.').pop() || '').toLowerCase();
                          return allowedExtensions.includes(ext);
                        });
                        if (validFiles.length === 0) {
                          alert('Lütfen PDF, Word veya PowerPoint dosyaları yükleyin.');
                          e.target.value = '';
                          return;
                        }
                        let uploadsInBatch = 0;
                        const currentWeek = selectedWeekNumber;
                        const normalizedWeekNumber = parseWeekValue(currentWeek);
                        const uploadOne = async (f) => {
                          try {
                            const maxSize = 15 * 1024 * 1024; // 15MB
                            if (f.size > maxSize) {
                              throw new Error(`Dosya boyutu 15MB'dan büyük olamaz. Mevcut boyut: ${(f.size / (1024 * 1024)).toFixed(2)}MB`);
                            }
                            const currentWeekNotes = weekNotes[normalizedWeekNumber] || [];
                            if (currentWeekNotes.length + uploadsInBatch >= 3) {
                              throw new Error(`Bu hafta için maksimum 3 dosya yüklenebilir. Mevcut dosya sayısı: ${currentWeekNotes.length}`);
                            }
                            const form = new FormData();
                            form.append('faculty', courseDetails.enstitu_fakulte || 'Fakulte');
                            form.append('department', courseDetails.program_name || 'Program');
                            form.append('course', courseDetails.course_code || courseDetails.course_name || 'Ders');
                            form.append('week', `${currentWeek}. Hafta`);
                            form.append('file', f);
                            form.append('userType', userType || 'admin');
                            form.append('program_id', programId);
                            const res = await fetch(API_ENDPOINTS.DRIVE_UPLOAD_PDF, {
                              method: 'POST',
                              headers: {
                                'User-Type': userType || 'akademisyen',
                              },
                              body: form,
                            });
                            if (!res.ok) {
                              const errorData = await res.json().catch(() => ({ error: 'Yükleme başarısız' }));
                              throw new Error(errorData.error || 'Yükleme başarısız');
                            }
                            uploadsInBatch += 1;
                            await fetchWeeklyNotes();
                            alert('Dosya başarıyla yüklendi');
                          } catch (err) {
                            console.error('Dosya yükleme hatası:', err);
                            alert(`Dosya yüklenemedi: ${err.message}`);
                          }
                        };
                        (async () => {
                          for (const f of validFiles) {
                            // eslint-disable-next-line no-await-in-loop
                            await uploadOne(f);
                          }
                        })();
                        e.target.value = '';
                      }}
                      id={`week-notes-modal-${selectedWeekNumber}`}
                      className="week-notes-input"
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor={`week-notes-modal-${selectedWeekNumber}`}
                      className="week-notes-upload-btn"
                    >
                      Dosya Yükle
                    </label>
                  </div>
                )}
                {(() => {
                  const normalizedWeek = parseWeekValue(selectedWeekNumber);
                  const weekFiles = (weekNotes[normalizedWeek] || []).filter(file => {
                    const displayName = (file.name || file.file_name || '').trim();
                    return displayName !== '';
                  });
                  return weekFiles.length === 0 ? (
                    <p className="week-modal-empty">Bu hafta için dosya eklenmemiş.</p>
                  ) : (
                    <ul className="week-modal-list">
                      {weekFiles.map((file) => {
                      const displayName = file.name || file.file_name || 'İsimsiz Dosya';
                      // PDF klasöründen gelen notlar için URL'yi direkt kullan
                      const viewUrl = (file.url || `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-notes/${file.id}/view`) + `?t=${file.upload_date || file.id}`;
                      const downloadUrl = (file.url || `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-notes/${file.id}/download`) + `?t=${file.upload_date || file.id}`;
                      return (
                        <li key={file.id || `file_${file.name}_${Math.random()}`}>
                          <span style={{ flex: 1, wordBreak: 'break-word' }}>{displayName}</span>
                          <div className="week-modal-actions">
                            <button
                              type="button"
                              className="week-modal-button"
                              onClick={() => window.open(viewUrl, '_blank')}
                            >
                              <span className="icon">👁️</span>
                              Görüntüle
                            </button>
                            <button
                              type="button"
                              className="week-modal-button"
                              onClick={() => {
                                if (file.url) {
                                  // PDF klasöründen gelen notlar için direkt indir
                                  const link = document.createElement('a');
                                  link.href = file.url;
                                  link.download = displayName;
                                  link.target = '_blank';
                                  link.click();
                                } else {
                                  window.open(downloadUrl, '_blank');
                                }
                              }}
                            >
                              <span className="icon">⬇️</span>
                              İndir
                            </button>
                            {(userType === 'admin' || userType === 'akademisyen') && !isReadOnly() && (
                              <button
                                type="button"
                                className="week-modal-button danger"
                                onClick={async () => {
                                  if (!window.confirm(`${displayName} dosyasını silmek istediğinize emin misiniz?`)) {
                                    return;
                                  }
                                  try {
                                    const response = await fetch(
                                      `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-notes/${file.id}`,
                                      {
                                        method: 'DELETE',
                                        headers: {
                                          'User-Type': userType || 'admin'
                                        }
                                      }
                                    );
                                    if (response.ok) {
                                      await fetchWeeklyNotes();
                                      alert('Dosya başarıyla silindi');
                                    } else {
                                      const errorData = await response.json().catch(() => ({ error: 'Silme başarısız' }));
                                      alert(errorData.error || 'Silme başarısız');
                                    }
                                  } catch (err) {
                                    console.error('Dosya silme hatası:', err);
                                    alert('Dosya silinirken bir hata oluştu');
                                  }
                                }}
                              >
                                🗑️ Sil
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                    </ul>
                  );
                })()}
              </div>
              <div className="week-modal-section">
                <h5>Video Linkleri</h5>
                {(() => {
                  const normalizedWeek = parseWeekValue(selectedWeekNumber);
                  const weekVids = weekVideos[normalizedWeek] || [];
                  return weekVids.length === 0 ? (
                    <p className="week-modal-empty">Bu hafta için video linki yok.</p>
                  ) : (
                    <ul className="week-modal-list">
                      {weekVids.map((video) => (
                      <li key={video.id}>
                        <a href={video.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, wordBreak: 'break-word' }}>
                          {video.title || video.url}
                        </a>
                        {userType === 'admin' && !isReadOnly() && (
                          <div className="week-modal-actions">
                            <button
                              type="button"
                              className="week-modal-button danger"
                              onClick={() => handleDeleteVideoLink(selectedWeekNumber, video.id)}
                            >
                              🗑️ Sil
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetails;
